import { useState, useEffect, useRef } from "react";
import {
  X,
  ChevronDown,
  Plus,
  Image,
  Video,
  DollarSign,
  Tags,
  Users,
  BookOpen,
  HelpCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Select } from "@/components/admin/ui/Select";
import { Toggle } from "@/components/admin/ui/Toggle";
import { Card } from "@/components/admin/ui/Card";
import { useToast } from "@/components/admin/ui/Toast";
import logo from "@/assets/logo.png";

interface CourseBuilderProps {
  onClose: () => void;
  onSave?: (course: any) => void;
  courseId?: number | null;
  courseData?: any;
}

const AccordionItem = ({
  title,
  icon: Icon,
  open,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ElementType;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <div className="border-b border-gray-100 dark:border-white/5 last:border-0">
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50/50 dark:hover:bg-white/3 transition-colors"
    >
      <div className="flex items-center gap-2.5 text-gray-700 dark:text-[#D1D5DB]">
        <Icon className="h-4 w-4 text-gray-500 dark:text-[#A0A5AD]" />
        <span className="text-sm font-medium">{title}</span>
      </div>
      <ChevronDown
        className={`h-4 w-4 text-gray-400 dark:text-[#80858D] transition-transform ${
          open ? "rotate-180" : ""
        }`}
      />
    </button>
    {open && <div className="px-5 pb-4">{children}</div>}
  </div>
);

const VisualEditor = ({
  value,
  onChange,
  editorRef,
  placeholder,
  className = "",
}: {
  value: string;
  onChange: (val: string) => void;
  editorRef: React.RefObject<any>;
  placeholder?: string;
  className?: string;
}) => {
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value && !isEditingRef.current) {
      editorRef.current.innerHTML = value;
    }
  }, [value, editorRef]);

  return (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      onFocus={() => {
        isEditingRef.current = true;
      }}
      onBlur={(e) => {
        isEditingRef.current = false;
        onChange(e.currentTarget.innerHTML);
      }}
      onInput={(e) => {
        onChange(e.currentTarget.innerHTML);
      }}
      className={`w-full p-4 text-sm !text-black bg-white focus:outline-none min-h-[180px] overflow-y-auto border-none prose prose-sm max-w-none ${className}`}
      style={{
        outline: "none",
        minHeight: "180px",
      }}
      data-placeholder={placeholder}
    />
  );
};

const getEmbedUrl = (url: string) => {
  if (!url) return null;
  // Only generate embed URLs for YouTube and Vimeo
  const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
  if (ytMatch && ytMatch[1]) {
    return `https://www.youtube.com/embed/${ytMatch[1]}?modestbranding=1&rel=0&controls=1&showinfo=0&iv_load_policy=3`;
  }
  const vimeoMatch = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/i);
  if (vimeoMatch && vimeoMatch[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  // For direct video URLs (mp4, blob, uploaded files), return null
  // so the <video> tag is used instead of an <iframe>
  return null;
};

export function CourseBuilder({ onClose, onSave, courseId, courseData }: CourseBuilderProps) {
  const [step, setStep] = useState<"basics" | "curriculum" | "additional">(
    "basics"
  );
  const [optionsTab, setOptionsTab] = useState<
    "general" | "drip" | "enrollment"
  >("general");
  const [isSaving, setIsSaving] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState({
    visibility: true,
    featured: false,
    intro: false,
    pricing: true,
    categories: false,
    tags: false,
    author: false,
    instructors: false,
  });

  const getRestUrl = (endpoint: string) => {
    const restUrl = (window as any).hubInstructor?.rest_url || (window as any).shLmsAdmin?.rest_url || (window as any).wpApiSettings?.root || "/wp-json/";
    const baseUrl = restUrl.endsWith("/") ? restUrl : restUrl + "/";
    return baseUrl + (endpoint.startsWith("/") ? endpoint.substring(1) : endpoint);
  };

  const [title, setTitle] = useState(courseData?.title || "");
  const [category, setCategory] = useState(courseData?.category || "এসএসসি");
  const [regularPrice, setRegularPrice] = useState(courseData?.regularPrice?.toString() || courseData?.price?.toString() || "1500");
  const [salePrice, setSalePrice] = useState(courseData?.salePrice?.toString() || "1500");
  const [pricingModel, setPricingModel] = useState(courseData?.price === 0 ? "free" : "paid");
  const [visibility, setVisibility] = useState(courseData?.visibility || "public");
  const [difficulty, setDifficulty] = useState(courseData?.difficulty || "beginner");
  const [isPublic, setIsPublic] = useState(courseData?.isPublic ?? true);
  const [selectedAuthor, setSelectedAuthor] = useState(
    courseData?.author || "Sarah Johnson"
  );
  const [courseUrl, setCourseUrl] = useState(courseData?.courseUrl || "ssc-2027-course");
  const [description, setDescription] = useState(courseData?.description || "");
  const [thumbnail, setThumbnail] = useState(courseData?.thumbnail || "");
  const [promoVideoUrl, setPromoVideoUrl] = useState(courseData?.promoVideoUrl || courseData?.video_path || "");
  const isUploadedVideo = (url: string) => {
    if (!url) return false;
    return url.includes("/wp-content/uploads/") || url.startsWith("blob:") || url.startsWith("data:");
  };
  const [promoVideoSource, setPromoVideoSource] = useState<"upload" | "url">(
    isUploadedVideo(courseData?.promoVideoUrl || courseData?.video_path || "") ? "upload" : "url"
  );
  const [promoVideoHours, setPromoVideoHours] = useState<number>(courseData?.promoVideoHours ? Number(courseData.promoVideoHours) : 0);
  const [promoVideoMinutes, setPromoVideoMinutes] = useState<number>(courseData?.promoVideoMinutes ? Number(courseData.promoVideoMinutes) : 0);
  const [promoVideoSeconds, setPromoVideoSeconds] = useState<number>(courseData?.promoVideoSeconds ? Number(courseData.promoVideoSeconds) : 0);
  const [thumbnailId, setThumbnailId] = useState<number | null>(
    courseData?.thumbnail_id ? Number(courseData.thumbnail_id) : null
  );
  const [enableContentDrip, setEnableContentDrip] = useState(courseData?.enableContentDrip || false);
  const [dripType, setDripType] = useState(courseData?.dripType || "date");
  const [enableEnrollment, setEnableEnrollment] = useState(courseData?.enableEnrollment ?? true);
  const [maxStudents, setMaxStudents] = useState<number>(
    courseData?.maxStudents !== undefined ? courseData.maxStudents : 100
  );
  const [unlimitedStudents, setUnlimitedStudents] = useState<boolean>(
    courseData?.maxStudents === 0
  );

  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const lessonContentRef = useRef<HTMLTextAreaElement>(null);
  const descriptionVisualRef = useRef<HTMLDivElement>(null);
  const lessonContentVisualRef = useRef<HTMLDivElement>(null);
  const [descriptionMode, setDescriptionMode] = useState<"visual" | "code">("visual");

  const formatTextarea = (
    ref: React.RefObject<HTMLTextAreaElement | null>,
    tool: string,
    value: string,
    setValue: (val: string) => void
  ) => {
    const textarea = ref.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let formatted = "";
    switch (tool) {
      case "B":
        formatted = `<strong>${selectedText || "bold text"}</strong>`;
        break;
      case "I":
        formatted = `<em>${selectedText || "italic text"}</em>`;
        break;
      case "U":
        formatted = `<u>${selectedText || "underlined text"}</u>`;
        break;
      case "S":
        formatted = `<s>${selectedText || "strikethrough text"}</s>`;
        break;
      case "Link":
        const url = prompt("Enter the URL:", "https://");
        if (url === null) return;
        formatted = `<a href="${url}">${selectedText || "Link Text"}</a>`;
        break;
      case "Image":
        const imgUrl = prompt("Enter the Image URL:", "https://");
        if (imgUrl === null) return;
        formatted = `<img src="${imgUrl}" alt="${selectedText || "Image Description"}" />`;
        break;
      default:
        return;
    }

    const newValue = value.substring(0, start) + formatted + value.substring(end);
    setValue(newValue);

    setTimeout(() => {
      textarea.focus();
      const offset = formatted.length - selectedText.length;
      textarea.setSelectionRange(start, end + offset);
    }, 0);
  };

  const handleDescriptionFormat = (tool: string) => {
    if (descriptionMode === "visual") {
      let command = "";
      let value = "";
      switch (tool) {
        case "B": command = "bold"; break;
        case "I": command = "italic"; break;
        case "U": command = "underline"; break;
        case "S": command = "strikeThrough"; break;
        case "Link":
          const url = prompt("Enter the URL:", "https://");
          if (url) {
            command = "createLink";
            value = url;
          }
          break;
        case "Image":
          const imgUrl = prompt("Enter the Image URL:", "https://");
          if (imgUrl) {
            command = "insertImage";
            value = imgUrl;
          }
          break;
        default: return;
      }
      if (command) {
        if (descriptionVisualRef.current) {
          descriptionVisualRef.current.focus();
        }
        document.execCommand(command, false, value);
        if (descriptionVisualRef.current) {
          setDescription(descriptionVisualRef.current.innerHTML);
        }
      }
    } else {
      formatTextarea(descriptionRef, tool, description, setDescription);
    }
  };

  const handleLessonFormat = (tool: string) => {
    if (lessonContentMode === "visual") {
      let command = "";
      let value = "";
      switch (tool) {
        case "B": command = "bold"; break;
        case "I": command = "italic"; break;
        case "U": command = "underline"; break;
        case "S": command = "strikeThrough"; break;
        case "Link":
          const url = prompt("Enter the URL:", "https://");
          if (url) {
            command = "createLink";
            value = url;
          }
          break;
        case "Image":
          const imgUrl = prompt("Enter the Image URL:", "https://");
          if (imgUrl) {
            command = "insertImage";
            value = imgUrl;
          }
          break;
        case "ul": command = "insertUnorderedList"; break;
        case "ol": command = "insertOrderedList"; break;
        case "≡L": command = "justifyLeft"; break;
        case "≡C": command = "justifyCenter"; break;
        case "≡R": command = "justifyRight"; break;
        default: return;
      }
      if (command) {
        if (lessonContentVisualRef.current) {
          lessonContentVisualRef.current.focus();
        }
        document.execCommand(command, false, value);
        if (lessonContentVisualRef.current) {
          setLessonContent(lessonContentVisualRef.current.innerHTML);
        }
      }
    } else {
      formatTextarea(lessonContentRef, tool, lessonContent, setLessonContent);
    }
  };

  const handleHeadingChange = (val: string) => {
    if (lessonContentMode === "visual") {
      let block = "p";
      if (val === "heading1") block = "h1";
      if (val === "heading2") block = "h2";
      if (val === "heading3") block = "h3";
      document.execCommand("formatBlock", false, `<${block}>`);
      if (lessonContentVisualRef.current) {
        setLessonContent(lessonContentVisualRef.current.innerHTML);
      }
    } else {
      let tag = "p";
      if (val === "heading1") tag = "h1";
      if (val === "heading2") tag = "h2";
      if (val === "heading3") tag = "h3";
      const textarea = lessonContentRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = lessonContent.substring(start, end);
        const formatted = `<${tag}>${selectedText || ""}</${tag}>`;
        const newValue = lessonContent.substring(0, start) + formatted + lessonContent.substring(end);
        setLessonContent(newValue);
      }
    }
  };

  // Curriculum States
  const [topics, setTopics] = useState<any[]>(() => {
    let list = courseData?.topicsList;
    if (list) {
      if (typeof list === "string") {
        try {
          list = JSON.parse(list);
        } catch (e) {
          console.error("Failed to parse courseData.topicsList JSON string:", e);
          list = null;
        }
      }
      if (Array.isArray(list)) {
        return list;
      }
    }
    return [
      {
        id: 1,
        title: "Introduction",
        summary: "Welcome to the course outline.",
        lessons: [
          { id: 101, title: "Introduction to the Course", duration: "15 min" },
          { id: 102, title: "Setting Up the Environment", duration: "15 min" }
        ],
        quizzes: []
      }
    ];
  });
  const [topicTitle, setTopicTitle] = useState("");
  const [topicDescription, setTopicDescription] = useState("");

  const [activeTopicIdForLesson, setActiveTopicIdForLesson] = useState<number | null>(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [lessonTitle, setLessonTitle] = useState("");
  const [lessonContent, setLessonContent] = useState("");
  const [lessonDuration, setLessonDuration] = useState("15 min");

  // New Lesson states for Shuddhoshikha LMS Schema compatibility
  const [editingLessonId, setEditingLessonId] = useState<number | null>(null);
  const [lessonContentMode, setLessonContentMode] = useState<"visual" | "code">("visual");
  const [lessonFeaturedImage, setLessonFeaturedImage] = useState<{ id?: number; url: string } | null>(null);
  const [lessonVideoSource, setLessonVideoSource] = useState<"upload" | "url">("upload");
  const [lessonVideoUrl, setLessonVideoUrl] = useState("");
  const [lessonVideoHours, setLessonVideoHours] = useState(0);
  const [lessonVideoMinutes, setLessonVideoMinutes] = useState(0);
  const [lessonVideoSeconds, setLessonVideoSeconds] = useState(0);
  const [lessonAttachments, setLessonAttachments] = useState<Array<{ id: number; name: string; url: string }>>([]);
  const [lessonIsFreePreview, setLessonIsFreePreview] = useState(false);

  // Zoom Live Lesson states
  const [showZoomSection, setShowZoomSection] = useState(false);
  const [zoomMeetingName, setZoomMeetingName] = useState("");
  const [zoomMeetingSummary, setZoomMeetingSummary] = useState("");
  const [zoomMeetingDate, setZoomMeetingDate] = useState("");
  const [zoomStartTime, setZoomStartTime] = useState("");
  const [zoomDuration, setZoomDuration] = useState(40);
  const [zoomDurationUnit, setZoomDurationUnit] = useState<"minutes" | "hours">("minutes");
  const [zoomTimezone, setZoomTimezone] = useState("Asia/Dhaka");
  const [zoomAutoRecording, setZoomAutoRecording] = useState("none");
  const [zoomPassword, setZoomPassword] = useState("");
  const [zoomShowPassword, setZoomShowPassword] = useState(false);
  const [zoomHost, setZoomHost] = useState("");
  const [lessonZoomMeeting, setLessonZoomMeeting] = useState<any>(null);

  // Quiz / MCQ Builder states
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [activeTopicIdForQuiz, setActiveTopicIdForQuiz] = useState<number | null>(null);
  const [quizTitle, setQuizTitle] = useState("");
  const [quizTimeLimit, setQuizTimeLimit] = useState(30);
  const [quizPassingGrade, setQuizPassingGrade] = useState(50);
  const [quizRandomize, setQuizRandomize] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<Array<{
    id: number;
    question: string;
    options: string[];
    correctAnswer: number;
    points: number;
  }>>([
    { id: Date.now(), question: "", options: ["", "", "", ""], correctAnswer: 0, points: 1 }
  ]);

  const [dbInstructors, setDbInstructors] = useState<any[]>([]);
  const [dbCategories, setDbCategories] = useState<any[]>([]);
  const [dbTags, setDbTags] = useState<any[]>([]);
  const [tagsInput, setTagsInput] = useState<string>(() => {
    if (typeof courseData?.tags === "string") {
      return courseData.tags;
    }
    if (Array.isArray(courseData?.tags)) {
      return courseData.tags.join(", ");
    }
    return "";
  });
  const [selectedInstructors, setSelectedInstructors] = useState<string[]>(() => {
    if (Array.isArray(courseData?.instructors)) {
      return courseData.instructors;
    }
    if (courseData?.author) {
      return [courseData.author];
    }
    return [];
  });
  const [isDripLoading, setIsDripLoading] = useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;
    const instructorNonce =
      (window as any).hubInstructor?.nonce ||
      adminNonce;

    if (!ajaxUrl || !(window as any).jQuery) return;

    // 1. Fetch Instructors
    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "GET",
      data: {
        action: "hub_get_instructors",
        security: instructorNonce
      },
      success: (res: any) => {
        if (res.success && Array.isArray(res.data)) {
          setDbInstructors(res.data);
          if (!courseData?.author && res.data.length > 0) {
            const firstInst = res.data[0].name;
            setSelectedAuthor(firstInst);
            setSelectedInstructors((prev) => {
              if (prev.length === 0) {
                return [firstInst];
              }
              return prev;
            });
          }
        }
      },
      error: (err: any) => {
        console.error("Failed to fetch instructors for CourseBuilder:", err);
      }
    });

    // 2. Fetch Categories (Batches)
    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_course_terms",
        nonce: adminNonce,
        taxonomy: "course_batch_category"
      },
      success: (res: any) => {
        if (res.success && Array.isArray(res.data)) {
          setDbCategories(res.data);
          if (!courseData?.category && res.data.length > 0) {
            setCategory(res.data[0].name);
          }
        }
      }
    });

    // 3. Fetch Tags (Class Tags)
    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_course_terms",
        nonce: adminNonce,
        taxonomy: "course_class_tag"
      },
      success: (res: any) => {
        if (res.success && Array.isArray(res.data)) {
          setDbTags(res.data);
        }
      }
    });
  }, []);

  // Auto-detect and populate video duration
  useEffect(() => {
    if (!lessonVideoUrl) return;

    // 1. Check if it's a local video / blob url / mp4
    const isLocalVideo = lessonVideoUrl.startsWith("blob:") || lessonVideoUrl.match(/\.(mp4|webm|ogg)$/i);
    if (isLocalVideo) {
      const tempVideo = document.createElement("video");
      tempVideo.src = lessonVideoUrl;
      tempVideo.onloadedmetadata = () => {
        const duration = tempVideo.duration;
        if (duration && duration > 0) {
          const hours = Math.floor(duration / 3600);
          const minutes = Math.floor((duration % 3600) / 60);
          const seconds = Math.floor(duration % 60);
          setLessonVideoHours(hours);
          setLessonVideoMinutes(minutes);
          setLessonVideoSeconds(seconds);
          showToast(`Video duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
        }
      };
      return;
    }

    // 2. Check if it's YouTube
    const ytMatch = lessonVideoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
    if (ytMatch && ytMatch[1]) {
      // Load YouTube IFrame Player API if not exists
      if (!(window as any).YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
      }

      // Check periodically for YT object and initialize player
      let checkInterval = setInterval(() => {
        if ((window as any).YT && (window as any).YT.Player) {
          clearInterval(checkInterval);
          
          // Create temporary div for player to load metadata
          const tempDiv = document.createElement("div");
          tempDiv.style.display = "none";
          document.body.appendChild(tempDiv);

          new (window as any).YT.Player(tempDiv, {
            videoId: ytMatch[1],
            height: "0",
            width: "0",
            playerVars: {
              autoplay: 0,
              controls: 0,
              disablekb: 1,
              fs: 0,
              modestbranding: 1,
              rel: 0,
              showinfo: 0
            },
            events: {
              onReady: (event: any) => {
                const duration = event.target.getDuration();
                if (duration && duration > 0) {
                  const hours = Math.floor(duration / 3600);
                  const minutes = Math.floor((duration % 3600) / 60);
                  const seconds = Math.floor(duration % 60);
                  setLessonVideoHours(hours);
                  setLessonVideoMinutes(minutes);
                  setLessonVideoSeconds(seconds);
                  showToast(`YouTube duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
                  event.target.destroy();
                  tempDiv.remove();
                } else {
                  event.target.mute();
                  event.target.playVideo();
                }
              },
              onStateChange: (event: any) => {
                if (event.data === 1) {
                  const duration = event.target.getDuration();
                  if (duration && duration > 0) {
                    const hours = Math.floor(duration / 3600);
                    const minutes = Math.floor((duration % 3600) / 60);
                    const seconds = Math.floor(duration % 60);
                    setLessonVideoHours(hours);
                    setLessonVideoMinutes(minutes);
                    setLessonVideoSeconds(seconds);
                    showToast(`YouTube duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
                  }
                  event.target.destroy();
                  tempDiv.remove();
                }
              }
            }
          });
        }
      }, 500);

      return () => clearInterval(checkInterval);
    }

    // 3. Check if it's Vimeo
    const vimeoMatch = lessonVideoUrl.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/i);
    if (vimeoMatch && vimeoMatch[1]) {
      fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoMatch[1]}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.duration) {
            const duration = parseInt(data.duration);
            const hours = Math.floor(duration / 3600);
            const minutes = Math.floor((duration % 3600) / 60);
            const seconds = Math.floor(duration % 60);
            setLessonVideoHours(hours);
            setLessonVideoMinutes(minutes);
            setLessonVideoSeconds(seconds);
            showToast(`Vimeo duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
          }
        })
        .catch((err) => console.error("Error fetching Vimeo duration:", err));
    }
  }, [lessonVideoUrl]);

  const openWpMediaLibrary = (
    type: "image" | "video" | "attachment",
    onSelect: (attachment: any) => void,
    fallbackInputId: string
  ) => {
    const wp = (window as any).wp;
    if (wp && wp.media) {
      const frame = wp.media({
        title: type === "image" 
          ? "Select or Upload Image" 
          : type === "video" 
            ? "Select or Upload Video" 
            : "Select or Upload File",
        button: {
          text: "Use this file",
        },
        library: {
          type: type === "image" 
            ? "image" 
            : type === "video" 
              ? "video" 
              : undefined,
        },
        multiple: false,
      });

      frame.on("select", () => {
        const attachment = frame.state().get("selection").first().toJSON();
        onSelect(attachment);
      });

      frame.open();
    } else {
      document.getElementById(fallbackInputId)?.click();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Save old thumbnail so we can revert on failure
    const previousThumbnail = thumbnail;
    const previousThumbnailId = thumbnailId;

    // Show instant local preview
    const localUrl = URL.createObjectURL(file);
    setThumbnail(localUrl);

    showToast("Uploading featured image...", "info");

    // --- Method 1: Try REST API (wp/v2/media) ---
    let restSuccess = false;
    try {
      const restFormData = new FormData();
      restFormData.append("file", file);
      if (courseId) {
        restFormData.append("post", courseId.toString());
      }

      const nonce = (window as any).hubInstructor?.rest_nonce || (window as any).shLmsAdmin?.rest_nonce || (window as any).hubInstructor?.nonce;
      
      const headers: Record<string, string> = {};
      if (nonce) {
        headers["X-WP-Nonce"] = nonce;
      }

      const response = await fetch(getRestUrl("wp/v2/media"), {
        method: "POST",
        body: restFormData,
        headers: headers,
        credentials: "same-origin",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `REST upload failed with status ${response.status}`);
      }

      const media = await response.json();
      console.log("Featured image uploaded via REST API:", media);
      const imageUrl = media.source_url || media.guid?.rendered || localUrl;
      setThumbnail(imageUrl);
      setThumbnailId(Number(media.id));
      showToast("Featured image uploaded successfully!", "success");
      restSuccess = true;
    } catch (restErr: any) {
      console.warn("REST API upload failed, trying AJAX fallback:", restErr.message);
    }

    // --- Method 2: Fallback to AJAX (chorcha_upload_media) ---
    if (!restSuccess) {
      try {
        const ajaxUrl =
          (window as any).shLmsAdmin?.ajax_url ||
          (window as any).hubInstructor?.ajax_url ||
          (window as any).ajaxurl;
        const adminNonce =
          (window as any).shLmsAdmin?.nonce ||
          (window as any).chorchaAdmin?.nonce;

        if (!ajaxUrl) {
          throw new Error("WordPress AJAX endpoint not found");
        }

        const ajaxFormData = new FormData();
        ajaxFormData.append("action", "chorcha_upload_media");
        ajaxFormData.append("nonce", adminNonce || "");
        ajaxFormData.append("file", file);
        if (courseId) {
          ajaxFormData.append("post_id", courseId.toString());
        }

        const response = await fetch(ajaxUrl, {
          method: "POST",
          body: ajaxFormData,
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`AJAX upload failed with status ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          console.log("Featured image uploaded via AJAX fallback:", result.data);
          setThumbnail(result.data.url);
          setThumbnailId(Number(result.data.id));
          showToast("Featured image uploaded successfully!", "success");
        } else {
          throw new Error(result.data?.message || "AJAX upload returned an error");
        }
      } catch (ajaxErr: any) {
        console.error("AJAX fallback upload also failed:", ajaxErr);
        // Revert to previous thumbnail so the save handler won't be blocked
        setThumbnail(previousThumbnail);
        setThumbnailId(previousThumbnailId);
        showToast(`Image upload failed: ${ajaxErr.message}. Previous image preserved.`, "warning");
      }
    }
  };

  const handlePromoVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Save old promo video URL so we can revert on failure
    const previousPromoVideoUrl = promoVideoUrl;

    // Show instant local preview
    const localUrl = URL.createObjectURL(file);
    setPromoVideoUrl(localUrl);

    showToast("Uploading promo video...", "info");

    // --- Method 1: Try REST API (wp/v2/media) ---
    let restSuccess = false;
    try {
      const restFormData = new FormData();
      restFormData.append("file", file);
      if (courseId) {
        restFormData.append("post", courseId.toString());
      }

      const nonce = (window as any).hubInstructor?.rest_nonce || (window as any).shLmsAdmin?.rest_nonce || (window as any).hubInstructor?.nonce;
      
      const headers: Record<string, string> = {};
      if (nonce) {
        headers["X-WP-Nonce"] = nonce;
      }

      const response = await fetch(getRestUrl("wp/v2/media"), {
        method: "POST",
        body: restFormData,
        headers: headers,
        credentials: "same-origin",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `REST upload failed with status ${response.status}`);
      }

      const media = await response.json();
      console.log("Promo video uploaded via REST API:", media);
      const videoUrl = media.source_url || media.guid?.rendered || localUrl;
      setPromoVideoUrl(videoUrl);
      showToast("Promo video uploaded successfully!", "success");
      restSuccess = true;
    } catch (restErr: any) {
      console.warn("REST API upload failed, trying AJAX fallback:", restErr.message);
    }

    // --- Method 2: Fallback to AJAX (chorcha_upload_media) ---
    if (!restSuccess) {
      try {
        const ajaxUrl =
          (window as any).shLmsAdmin?.ajax_url ||
          (window as any).hubInstructor?.ajax_url ||
          (window as any).ajaxurl;
        const adminNonce =
          (window as any).shLmsAdmin?.nonce ||
          (window as any).chorchaAdmin?.nonce;

        if (!ajaxUrl) {
          throw new Error("WordPress AJAX endpoint not found");
        }

        const ajaxFormData = new FormData();
        ajaxFormData.append("action", "chorcha_upload_media");
        ajaxFormData.append("nonce", adminNonce || "");
        ajaxFormData.append("file", file);
        if (courseId) {
          ajaxFormData.append("post_id", courseId.toString());
        }

        const response = await fetch(ajaxUrl, {
          method: "POST",
          body: ajaxFormData,
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`AJAX upload failed with status ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          console.log("Promo video uploaded via AJAX fallback:", result.data);
          setPromoVideoUrl(result.data.url);
          showToast("Promo video uploaded successfully!", "success");
        } else {
          throw new Error(result.data?.message || "AJAX upload returned an error");
        }
      } catch (ajaxErr: any) {
        console.error("AJAX fallback upload also failed:", ajaxErr);
        // Revert to previous promo video URL so the save handler won't be blocked
        setPromoVideoUrl(previousPromoVideoUrl);
        showToast(`Video upload failed: ${ajaxErr.message}. Previous video preserved.`, "warning");
      }
    }
  };

  // Auto-detect and populate promo video duration
  useEffect(() => {
    if (!promoVideoUrl) return;

    // 1. Check if it's a local video / blob url / mp4
    const isLocalVideo = promoVideoUrl.startsWith("blob:") || promoVideoUrl.match(/\.(mp4|webm|ogg)$/i);
    if (isLocalVideo) {
      const tempVideo = document.createElement("video");
      tempVideo.src = promoVideoUrl;
      tempVideo.onloadedmetadata = () => {
        const duration = tempVideo.duration;
        if (duration && duration > 0) {
          const hours = Math.floor(duration / 3600);
          const minutes = Math.floor((duration % 3600) / 60);
          const seconds = Math.floor(duration % 60);
          setPromoVideoHours(hours);
          setPromoVideoMinutes(minutes);
          setPromoVideoSeconds(seconds);
          showToast(`Promo video duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
        }
      };
      return;
    }

    // 2. Check if it's YouTube
    const ytMatch = promoVideoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i);
    if (ytMatch && ytMatch[1]) {
      if (!(window as any).YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
      }

      let checkInterval = setInterval(() => {
        if ((window as any).YT && (window as any).YT.Player) {
          clearInterval(checkInterval);
          
          const tempDiv = document.createElement("div");
          tempDiv.style.display = "none";
          document.body.appendChild(tempDiv);

          new (window as any).YT.Player(tempDiv, {
            videoId: ytMatch[1],
            height: "0",
            width: "0",
            playerVars: {
              autoplay: 0,
              controls: 0,
              disablekb: 1,
              fs: 0,
              modestbranding: 1,
              rel: 0,
              showinfo: 0
            },
            events: {
              onReady: (event: any) => {
                const duration = event.target.getDuration();
                if (duration && duration > 0) {
                  const hours = Math.floor(duration / 3600);
                  const minutes = Math.floor((duration % 3600) / 60);
                  const seconds = Math.floor(duration % 60);
                  setPromoVideoHours(hours);
                  setPromoVideoMinutes(minutes);
                  setPromoVideoSeconds(seconds);
                  showToast(`YouTube duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
                  event.target.destroy();
                  tempDiv.remove();
                } else {
                  event.target.mute();
                  event.target.playVideo();
                }
              },
              onStateChange: (event: any) => {
                if (event.data === 1) {
                  const duration = event.target.getDuration();
                  if (duration && duration > 0) {
                    const hours = Math.floor(duration / 3600);
                    const minutes = Math.floor((duration % 3600) / 60);
                    const seconds = Math.floor(duration % 60);
                    setPromoVideoHours(hours);
                    setPromoVideoMinutes(minutes);
                    setPromoVideoSeconds(seconds);
                    showToast(`YouTube duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
                  }
                  event.target.destroy();
                  tempDiv.remove();
                }
              }
            }
          });
        }
      }, 500);

      return () => clearInterval(checkInterval);
    }

    // 3. Check if it's Vimeo
    const vimeoMatch = promoVideoUrl.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/i);
    if (vimeoMatch && vimeoMatch[1]) {
      fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoMatch[1]}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.duration) {
            const duration = parseInt(data.duration);
            const hours = Math.floor(duration / 3600);
            const minutes = Math.floor((duration % 3600) / 60);
            const seconds = Math.floor(duration % 60);
            setPromoVideoHours(hours);
            setPromoVideoMinutes(minutes);
            setPromoVideoSeconds(seconds);
            showToast(`Vimeo duration auto-filled: ${hours}h ${minutes}m ${seconds}s`, "success");
          }
        })
        .catch((err) => console.error("Error fetching Vimeo duration:", err));
    }
  }, [promoVideoUrl]);

  const handleLessonImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    setLessonFeaturedImage({ url: localUrl });

    const formData = new FormData();
    formData.append("file", file);

    try {
      showToast("Uploading lesson featured image...", "info");
      const nonce = (window as any).hubInstructor?.rest_nonce || (window as any).shLmsAdmin?.rest_nonce || (window as any).hubInstructor?.nonce;
      const headers: Record<string, string> = {};
      if (nonce) {
        headers["X-WP-Nonce"] = nonce;
      }

      const response = await fetch(getRestUrl("wp/v2/media"), {
        method: "POST",
        body: formData,
        headers: headers,
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const media = await response.json();
      const imageUrl = media.source_url || media.guid?.rendered || localUrl;
      setLessonFeaturedImage({ id: media.id, url: imageUrl });
      showToast("Lesson featured image uploaded!", "success");
    } catch (err: any) {
      console.error(err);
      showToast("Upload failed, using local preview.", "warning");
    }
  };

  const handleLessonVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    setLessonVideoUrl(localUrl);

    const formData = new FormData();
    formData.append("file", file);

    try {
      showToast("Uploading lesson video...", "info");
      const nonce = (window as any).hubInstructor?.rest_nonce || (window as any).shLmsAdmin?.rest_nonce || (window as any).hubInstructor?.nonce;
      const headers: Record<string, string> = {};
      if (nonce) {
        headers["X-WP-Nonce"] = nonce;
      }

      const response = await fetch(getRestUrl("wp/v2/media"), {
        method: "POST",
        body: formData,
        headers: headers,
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const media = await response.json();
      const videoUrl = media.source_url || media.guid?.rendered || localUrl;
      setLessonVideoUrl(videoUrl);
      showToast("Lesson video uploaded!", "success");
    } catch (err: any) {
      console.error(err);
      showToast("Video upload failed, using local preview.", "warning");
    }
  };

  const handleLessonAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    const mockAttachment = { id: Date.now(), name: file.name, url: localUrl };
    setLessonAttachments((prev) => [...prev, mockAttachment]);

    const formData = new FormData();
    formData.append("file", file);

    try {
      showToast("Uploading exercise file...", "info");
      const nonce = (window as any).hubInstructor?.rest_nonce || (window as any).shLmsAdmin?.rest_nonce || (window as any).hubInstructor?.nonce;
      const headers: Record<string, string> = {};
      if (nonce) {
        headers["X-WP-Nonce"] = nonce;
      }

      const response = await fetch(getRestUrl("wp/v2/media"), {
        method: "POST",
        body: formData,
        headers: headers,
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const media = await response.json();
      const attachmentUrl = media.source_url || media.guid?.rendered || localUrl;
      
      setLessonAttachments((prev) =>
        prev.map((att) =>
          att.name === file.name ? { id: media.id, name: file.name, url: attachmentUrl } : att
        )
      );
      showToast("Exercise file uploaded!", "success");
    } catch (err: any) {
      console.error(err);
      showToast("Attachment upload failed, using local preview.", "warning");
    }
  };

  const handleDripToggle = async (checked: boolean) => {
    setEnableContentDrip(checked);

    if (!courseId) {
      return;
    }

    setIsDripLoading(true);
    try {
      showToast("Updating Content Drip settings...", "info");
      const nonce = (window as any).hubInstructor?.rest_nonce || (window as any).shLmsAdmin?.rest_nonce || (window as any).hubInstructor?.nonce;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (nonce) {
        headers["X-WP-Nonce"] = nonce;
      }

      const response = await fetch(getRestUrl(`tutor/v1/courses/${courseId}`), {
        method: "PATCH",
        headers: headers,
        credentials: "same-origin",
        body: JSON.stringify({
          meta: {
            _tutor_enable_content_drip: checked ? "yes" : "no",
          },
          _tutor_enable_content_drip: checked,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server returned status ${response.status}`);
      }

      showToast("Content Drip settings updated!", "success");
    } catch (err: any) {
      console.error("Content Drip Update Error:", err);
      showToast(`Failed to sync Content Drip with server: ${err.message}`, "warning");
    } finally {
      setIsDripLoading(false);
    }
  };

  const handleAddTopic = (e: React.FormEvent) => {
    e.preventDefault();
    if (!topicTitle.trim()) {
      showToast("Please enter a topic title", "error");
      return;
    }
    const newTopic = {
      id: Date.now(),
      title: topicTitle.trim(),
      summary: topicDescription.trim(),
      lessons: [],
      quizzes: []
    };
    setTopics((prev) => [...prev, newTopic]);
    setTopicTitle("");
    setTopicDescription("");
    showToast("Topic added successfully!", "success");
  };

  const handleDeleteTopic = (id: number) => {
    setTopics((prev) => prev.filter((t) => t.id !== id));
    showToast("Topic deleted.", "error");
  };

  const moveTopic = (idx: number, dir: "up" | "down") => {
    const newIdx = dir === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= topics.length) return;
    const newTopics = [...topics];
    const temp = newTopics[idx];
    newTopics[idx] = newTopics[newIdx];
    newTopics[newIdx] = temp;
    setTopics(newTopics);
  };

  const handleAddLesson = (e: React.FormEvent) => {
    e.preventDefault();
    if (!lessonTitle.trim()) {
      showToast("Please enter a lesson title", "error");
      return;
    }
    if (activeTopicIdForLesson === null) return;

    let zoomData = lessonZoomMeeting;
    if (showZoomSection) {
      if (!zoomMeetingName || !zoomMeetingDate || !zoomStartTime || !zoomPassword || !zoomHost) {
        showToast("Please fill all required Zoom meeting fields.", "error");
        return;
      }
      zoomData = {
        meetingName: zoomMeetingName,
        summary: zoomMeetingSummary,
        date: zoomMeetingDate,
        startTime: zoomStartTime,
        duration: zoomDuration,
        durationUnit: zoomDurationUnit,
        timezone: zoomTimezone,
        autoRecording: zoomAutoRecording,
        password: zoomPassword,
        host: zoomHost,
      };
    }

    const playbackTotalSeconds = (lessonVideoHours * 3600) + (lessonVideoMinutes * 60) + lessonVideoSeconds;

    // Calculate display duration format: e.g. "1h 15m 30s" or "15 min"
    let formattedDuration = "15 min";
    if (playbackTotalSeconds > 0) {
      const parts = [];
      if (lessonVideoHours > 0) parts.push(`${lessonVideoHours}h`);
      if (lessonVideoMinutes > 0) parts.push(`${lessonVideoMinutes}m`);
      if (lessonVideoSeconds > 0) parts.push(`${lessonVideoSeconds}s`);
      formattedDuration = parts.join(" ");
    } else if (lessonDuration.trim()) {
      formattedDuration = lessonDuration.trim();
    }

    setTopics((prev) =>
      prev.map((topic) => {
        if (topic.id === activeTopicIdForLesson) {
          if (editingLessonId !== null) {
            // Edit existing lesson
            return {
              ...topic,
              lessons: topic.lessons.map((l: any) =>
                l.id === editingLessonId
                  ? {
                      ...l,
                      title: lessonTitle.trim(),
                      content: lessonContent.trim(),
                      contentMode: lessonContentMode,
                      duration: formattedDuration,
                      playbackHours: lessonVideoHours,
                      playbackMinutes: lessonVideoMinutes,
                      playbackSeconds: lessonVideoSeconds,
                      playbackSecondsTotal: playbackTotalSeconds,
                      featuredImage: lessonFeaturedImage,
                      videoSource: lessonVideoSource,
                      videoUrl: lessonVideoUrl,
                      attachments: lessonAttachments,
                      isFreePreview: lessonIsFreePreview,
                      zoomMeeting: zoomData
                    }
                  : l
              )
            };
          } else {
            // Add new lesson
            const newLesson = {
              id: Date.now(),
              title: lessonTitle.trim(),
              content: lessonContent.trim(),
              contentMode: lessonContentMode,
              duration: formattedDuration,
              playbackHours: lessonVideoHours,
              playbackMinutes: lessonVideoMinutes,
              playbackSeconds: lessonVideoSeconds,
              playbackSecondsTotal: playbackTotalSeconds,
              featuredImage: lessonFeaturedImage,
              videoSource: lessonVideoSource,
              videoUrl: lessonVideoUrl,
              attachments: lessonAttachments,
              isFreePreview: lessonIsFreePreview,
              zoomMeeting: zoomData
            };
            return {
              ...topic,
              lessons: [
                ...topic.lessons,
                newLesson,
              ],
            };
          }
        }
        return topic;
      })
    );

    const isEdit = editingLessonId !== null;

    // Reset lesson states
    setLessonTitle("");
    setLessonContent("");
    setLessonDuration("15 min");
    setLessonContentMode("visual");
    setLessonFeaturedImage(null);
    setLessonVideoSource("upload");
    setLessonVideoUrl("");
    setLessonVideoHours(0);
    setLessonVideoMinutes(0);
    setLessonVideoSeconds(0);
    setLessonAttachments([]);
    setLessonIsFreePreview(false);
    setEditingLessonId(null);
    // Reset zoom states
    setShowZoomSection(false);
    setZoomMeetingName("");
    setZoomMeetingSummary("");
    setZoomMeetingDate("");
    setZoomStartTime("");
    setZoomDuration(40);
    setZoomDurationUnit("minutes");
    setZoomTimezone("Asia/Dhaka");
    setZoomAutoRecording("none");
    setZoomPassword("");
    setZoomShowPassword(false);
    setZoomHost("");
    setLessonZoomMeeting(null);
    
    setShowLessonModal(false);
    setActiveTopicIdForLesson(null);
    showToast(isEdit ? "Lesson updated successfully!" : "Lesson added successfully!", "success");
  };

  const handleStartEditLesson = (topicId: number, lesson: any) => {
    setActiveTopicIdForLesson(topicId);
    setEditingLessonId(lesson.id);
    setLessonTitle(lesson.title || "");
    setLessonContent(lesson.content || "");
    setLessonDuration(lesson.duration || "15 min");
    setLessonContentMode(lesson.contentMode || "visual");
    setLessonFeaturedImage(lesson.featuredImage || null);
    setLessonVideoSource(lesson.videoSource || "upload");
    setLessonVideoUrl(lesson.videoUrl || "");
    setLessonVideoHours(lesson.playbackHours || 0);
    setLessonVideoMinutes(lesson.playbackMinutes || 0);
    setLessonVideoSeconds(lesson.playbackSeconds || 0);
    setLessonAttachments(lesson.attachments || []);
    setLessonIsFreePreview(lesson.isFreePreview || false);
    // Load zoom data if exists
    if (lesson.zoomMeeting) {
      setLessonZoomMeeting(lesson.zoomMeeting);
      setShowZoomSection(true);
      setZoomMeetingName(lesson.zoomMeeting.meetingName || "");
      setZoomMeetingSummary(lesson.zoomMeeting.summary || "");
      setZoomMeetingDate(lesson.zoomMeeting.date || "");
      setZoomStartTime(lesson.zoomMeeting.startTime || "");
      setZoomDuration(lesson.zoomMeeting.duration || 40);
      setZoomDurationUnit(lesson.zoomMeeting.durationUnit || "minutes");
      setZoomTimezone(lesson.zoomMeeting.timezone || "Asia/Dhaka");
      setZoomAutoRecording(lesson.zoomMeeting.autoRecording || "none");
      setZoomPassword(lesson.zoomMeeting.password || "");
      setZoomHost(lesson.zoomMeeting.host || "");
    } else {
      setLessonZoomMeeting(null);
      setShowZoomSection(false);
    }
    setShowLessonModal(true);
  };

  const handleAddQuiz = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quizTitle.trim()) {
      showToast("Please enter a quiz title", "error");
      return;
    }
    if (activeTopicIdForQuiz === null) return;

    // Validate at least one question
    const validQuestions = quizQuestions.filter(q => q.question.trim() !== "");
    if (validQuestions.length === 0) {
      showToast("Please add at least one question", "error");
      return;
    }

    const newQuiz = {
      id: Date.now(),
      title: quizTitle.trim(),
      timeLimit: quizTimeLimit,
      passingGrade: quizPassingGrade,
      randomize: quizRandomize,
      questions: validQuestions,
      questionCount: validQuestions.length,
      totalPoints: validQuestions.reduce((sum, q) => sum + q.points, 0),
    };

    setTopics((prev) =>
      prev.map((topic) => {
        if (topic.id === activeTopicIdForQuiz) {
          return {
            ...topic,
            quizzes: [...(topic.quizzes || []), newQuiz],
          };
        }
        return topic;
      })
    );

    // Reset quiz states
    setQuizTitle("");
    setQuizTimeLimit(30);
    setQuizPassingGrade(50);
    setQuizRandomize(false);
    setQuizQuestions([{ id: Date.now(), question: "", options: ["", "", "", ""], correctAnswer: 0, points: 1 }]);
    setShowQuizModal(false);
    setActiveTopicIdForQuiz(null);
    showToast("Quiz added successfully!", "success");
  };

  const handleDeleteQuiz = (topicId: number, quizId: number) => {
    setTopics((prev) =>
      prev.map((topic) => {
        if (topic.id === topicId) {
          return {
            ...topic,
            quizzes: (topic.quizzes || []).filter((q: any) => q.id !== quizId),
          };
        }
        return topic;
      })
    );
    showToast("Quiz removed.", "error");
  };

  const handleDeleteLesson = (topicId: number, lessonId: number) => {
    setTopics((prev) =>
      prev.map((topic) => {
        if (topic.id === topicId) {
          return {
            ...topic,
            lessons: topic.lessons.filter((l: any) => l.id !== lessonId),
          };
        }
        return topic;
      })
    );
    showToast("Lesson removed.", "error");
  };

  const moveLesson = (topicId: number, lessonIdx: number, dir: "up" | "down") => {
    setTopics((prev) =>
      prev.map((topic) => {
        if (topic.id === topicId) {
          const newIdx = dir === "up" ? lessonIdx - 1 : lessonIdx + 1;
          if (newIdx < 0 || newIdx >= topic.lessons.length) return topic;
          const newLessons = [...topic.lessons];
          const temp = newLessons[lessonIdx];
          newLessons[lessonIdx] = newLessons[newIdx];
          newLessons[newIdx] = temp;
          return { ...topic, lessons: newLessons };
        }
        return topic;
      })
    );
  };

  const handleSave = (status: "Publish" | "Draft") => {
    if (!title.trim()) {
      showToast("Please enter a course title", "error");
      return;
    }

    const calculatedPrice = pricingModel === "free"
      ? 0
      : (parseFloat(salePrice) || parseFloat(regularPrice) || 0);

    const authorObj = dbInstructors.find(
      (inst) => inst.name === selectedAuthor || inst.raw_name === selectedAuthor || inst.id?.toString() === selectedAuthor
    );
    const authorName = authorObj ? authorObj.name : selectedAuthor;
    const authorAvatarUrl = authorObj
      ? authorObj.avatar
      : selectedAuthor === "sarah"
      ? "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop"
      : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop";

    // Dynamic counts
    const totalTopics = topics.length;
    const totalLessons = topics.reduce((acc, t) => acc + (t.lessons?.length || 0), 0);
    const totalQuizzes = topics.reduce((acc, t) => acc + (t.quizzes?.length || 0), 0);

    const savedCourse = {
      ...courseData,
      id: courseId || courseData?.id || Date.now(),
      title: title.trim(),
      thumbnail: thumbnail || "",
      thumbnail_id: thumbnailId || null,
      category: category,
      price: calculatedPrice,
      author: authorName,
      authorAvatar: authorAvatarUrl,
      date: courseData?.date || new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
      status: status,
      // Dynamic counts mapping to main course object
      topics: totalTopics,
      lessons: totalLessons,
      quizzes: totalQuizzes,
      assignments: courseData?.assignments || 0,
      topicsList: topics,
      salePrice: parseFloat(salePrice) || 0,
      regularPrice: parseFloat(regularPrice) || 0,
      visibility,
      difficulty,
      isPublic,
      courseUrl,
      description,
      enableContentDrip,
      dripType,
      enableEnrollment,
      maxStudents: unlimitedStudents ? 0 : maxStudents,
      tags: tagsInput,
      instructors: selectedInstructors,
      promoVideoUrl: promoVideoUrl.trim(),
      promoVideoHours: promoVideoHours,
      promoVideoMinutes: promoVideoMinutes,
      promoVideoSeconds: promoVideoSeconds,
    };

    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      showToast("WordPress AJAX endpoint not found", "error");
      return;
    }

    // Note: We do NOT block save for blob promo video URLs.
    // If upload failed, the URL has been reverted to the previous valid value.
    // If it's still a blob (upload in progress), esc_url_raw will sanitize it on the backend.
    // The backend uses thumbnailId (attachment ID), not the thumbnail URL string.
    // If upload failed, thumbnailId still holds the previous valid value.

    setIsSaving(true);
    showToast(
      status === "Publish"
        ? "Publishing Course..."
        : "Saving Course as Draft...",
      "info"
    );

    // Map category name to term_id
    const categoryObj = dbCategories.find((cat) => cat.name === category);
    const categoryId = categoryObj ? categoryObj.term_id : 0;
    const categoryIds = categoryId ? [categoryId] : [];

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_save_course",
        nonce: adminNonce,
        post_id: courseId || "",
        title: title.trim(),
        url: courseUrl.trim(),
        description: description.trim(),
        status: status.toLowerCase(),
        regular_price: pricingModel === "free" ? 0 : regularPrice,
        sale_price: pricingModel === "free" ? 0 : salePrice,
        categories: categoryIds,
        tags: tagsInput,
        thumbnail_id: thumbnailId || "",
        topics_list: JSON.stringify(topics),
        instructors: selectedInstructors,
        difficulty: difficulty,
        is_public: isPublic ? 1 : 0,
        enable_enrollment: enableEnrollment ? 1 : 0,
        max_students: unlimitedStudents ? 0 : maxStudents,
        enable_content_drip: enableContentDrip ? 1 : 0,
        drip_type: dripType,
        promo_video: promoVideoUrl.trim(),
        promo_video_hours: promoVideoHours,
        promo_video_minutes: promoVideoMinutes,
        promo_video_seconds: promoVideoSeconds,
      },
      success: (res: any) => {
        setIsSaving(false);
        if (res.success) {
          showToast(res.data.message || "Course saved successfully!", "success");
          const finalCourseId = Number(res.data.post_id) || courseId || Date.now();
          const finalCourse = {
            ...savedCourse,
            id: finalCourseId,
          };
          if (onSave) {
            onSave(finalCourse);
            (window as any).refreshCourseListInstructors?.();
            (window as any).refreshInstructors?.();
          }
        } else {
          showToast(res.data || "Failed to save course", "error");
        }
      },
      error: (err: any) => {
        setIsSaving(false);
        console.error("Save Course Error:", err);
        showToast("An error occurred while saving the course", "error");
      }
    });
  };

  const steps = [
    { key: "basics", label: "Basics" },
    { key: "curriculum", label: "Curriculum" },
    { key: "additional", label: "Additional" },
  ];

  const toggleAccordion = (key: keyof typeof accordionOpen) => {
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50 transition-colors h-full overflow-hidden w-full">
      {/* Top Navigation */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={logo}
            alt="LMS"
            className="h-9 w-9 rounded-lg object-cover"
          />
          <span className="font-semibold text-gray-800">
            Course Builder
          </span>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
          {steps.map((s, idx) => (
            <button
              key={s.key}
              onClick={() => setStep(s.key as typeof step)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                step === s.key
                  ? "bg-white text-[#ff0000] shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="mr-1.5">{idx + 1}.</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => handleSave("Draft")} disabled={isSaving}>
            Save as Draft
          </Button>
          <div className="flex rounded-lg overflow-hidden shadow-sm">
            <Button size="sm" className="rounded-none rounded-l-lg" onClick={() => handleSave("Publish")} disabled={isSaving}>
              Publish
            </Button>
            <Button
              size="sm"
              className="rounded-none rounded-r-lg px-2 border-l border-red-400"
              disabled={isSaving}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-6 lg:p-8 pb-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ─── BASICS TAB ─── */}
          {step === "basics" && (
            <>
              <div className="lg:col-span-8 space-y-6">
                <Card>
                  <Input
                    label="Course Title"
                    placeholder="Enter course title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <div className="mt-4">
                    <label className="block text-sm font-medium !text-black mb-1.5">
                      Course URL
                    </label>
                    <div className="sh-light-box flex items-center rounded-lg px-3 py-2 text-sm">
                      <span className="font-medium">
                        https://lms.example.com/course/
                      </span>
                      <input
                        type="text"
                        value={courseUrl}
                        onChange={(e) => setCourseUrl(e.target.value)}
                        className="ml-1 flex-1 font-semibold"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium !text-black mb-1.5">
                      Description
                    </label>
                    <div className="sh-light-editor border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm">
                      <div className="editor-toolbar flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-1.5">
                        <div className="flex items-center gap-1">
                          {["B", "I", "U", "S", "Link", "Image"].map((tool) => (
                            <button
                              key={tool}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleDescriptionFormat(tool)}
                              className="px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-200 rounded transition-colors"
                            >
                              {tool}
                            </button>
                          ))}
                        </div>
                        {/* Visual / Code Tabs Toggle */}
                        <div className="toggle-container flex rounded-md p-0.5">
                          <button
                            type="button"
                            onClick={() => setDescriptionMode("visual")}
                            className={`px-2 py-0.5 text-[9px] font-bold rounded transition-all ${
                              descriptionMode === "visual"
                                ? "active"
                                : ""
                            }`}
                          >
                            Visual
                          </button>
                          <button
                            type="button"
                            onClick={() => setDescriptionMode("code")}
                            className={`px-2 py-0.5 text-[9px] font-bold rounded transition-all ${
                              descriptionMode === "code"
                                ? "active"
                                : ""
                            }`}
                          >
                            Code
                          </button>
                        </div>
                      </div>
                      {descriptionMode === "visual" ? (
                        <VisualEditor
                          value={description}
                          onChange={setDescription}
                          editorRef={descriptionVisualRef}
                          placeholder="Write a compelling course description..."
                          className="min-h-[160px]"
                        />
                      ) : (
                        <textarea
                          ref={descriptionRef}
                          rows={6}
                          placeholder="Write a compelling course description..."
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full p-4 text-sm focus:outline-none resize-none border-none font-mono text-xs"
                        />
                      )}
                    </div>
                  </div>
                </Card>

                <Card title="Options" description="Configure course behavior">
                  <div className="flex gap-0 border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden min-h-[240px]">
                    <div className="w-44 bg-gray-50 dark:bg-white/3 border-r border-gray-200 dark:border-white/10 flex flex-col">
                      {[
                        { key: "general", label: "General" },
                        { key: "drip", label: "Content Drip" },
                        { key: "enrollment", label: "Enrollment" },
                      ].map((tab) => (
                        <button
                          key={tab.key}
                          onClick={() =>
                            setOptionsTab(tab.key as typeof optionsTab)
                          }
                          className={`px-4 py-3 text-left text-sm font-medium transition-colors border-l-2 ${
                            optionsTab === tab.key
                              ? "bg-white dark:bg-[#1e293b] text-[#ff0000] border-[#ff0000]"
                              : "text-gray-600 dark:text-[#A0A5AD] border-transparent hover:bg-gray-100 dark:hover:bg-white/5"
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex-1 p-5">
                      {optionsTab === "general" && (
                        <div className="space-y-4">
                          <Select
                            label="Difficulty Level"
                            options={[
                              { value: "beginner", label: "Beginner" },
                              { value: "intermediate", label: "Intermediate" },
                              { value: "advanced", label: "Advanced" },
                            ]}
                            value={difficulty}
                            onChange={(val) => setDifficulty(val)}
                          />
                          <Toggle
                            label="Public Course"
                            description="Make this course visible to everyone"
                            checked={isPublic}
                            onChange={(checked) => setIsPublic(checked)}
                          />
                        </div>
                      )}
                      {optionsTab === "drip" && (
                        <div className="space-y-4">
                          <Toggle
                            label="Enable Content Drip"
                            description={isDripLoading ? "Syncing..." : "Release lessons progressively"}
                            checked={enableContentDrip}
                            onChange={handleDripToggle}
                            disabled={isDripLoading}
                          />
                          <Select
                            label="Drip Type"
                            options={[
                              { value: "date", label: "By Date" },
                              { value: "interval", label: "By Interval" },
                            ]}
                            value={dripType}
                            onChange={(val) => setDripType(val)}
                          />
                        </div>
                      )}
                      {optionsTab === "enrollment" && (
                        <div className="space-y-4">
                          <Toggle
                            label="Enable Enrollment"
                            description="Allow students to enroll"
                            checked={enableEnrollment}
                            onChange={(val) => setEnableEnrollment(val)}
                          />
                          <div className="space-y-2">
                            <Input
                              label="Maximum Students"
                              type="number"
                              value={unlimitedStudents ? "0" : maxStudents.toString()}
                              onChange={(e) => setMaxStudents(parseInt(e.target.value) || 0)}
                              disabled={unlimitedStudents}
                            />
                            <label className="flex items-center gap-2 text-sm text-gray-700 mt-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={unlimitedStudents}
                                onChange={(e) => setUnlimitedStudents(e.target.checked)}
                                className="rounded text-[#ff0000] focus:ring-[#ff0000]/30 border-gray-300"
                              />
                              <span className="font-medium">Unlimited Students</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              {/* Right Sidebar Accordions */}
              <div className="lg:col-span-4 space-y-6">
                <Card className="divide-y divide-gray-100 dark:divide-white/5">
                  <AccordionItem
                    title="Visibility"
                    icon={BookOpen}
                    open={accordionOpen.visibility}
                    onToggle={() => toggleAccordion("visibility")}
                  >
                    <Select
                      options={[
                        { value: "public", label: "Public" },
                        { value: "private", label: "Private" },
                        { value: "password", label: "Password Protected" },
                      ]}
                      value={visibility}
                      onChange={(val) => setVisibility(val)}
                    />
                  </AccordionItem>
                  <AccordionItem
                    title="Featured Image"
                    icon={Image}
                    open={accordionOpen.featured}
                    onToggle={() => toggleAccordion("featured")}
                  >
                    {thumbnail ? (
                      <div className="relative group rounded-lg overflow-hidden border border-gray-200">
                        <img src={thumbnail} alt="Featured" className="w-full h-32 object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => openWpMediaLibrary("image", (attachment) => { setThumbnail(attachment.url); setThumbnailId(attachment.id); }, "featured-image-upload")}
                            className="bg-white/90 hover:bg-white text-xs font-semibold text-gray-800 px-2.5 py-1.5 rounded cursor-pointer"
                          >
                            Change
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setThumbnail("");
                              setThumbnailId(null);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-xs font-semibold text-white px-2.5 py-1.5 rounded cursor-pointer"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => openWpMediaLibrary("image", (attachment) => { setThumbnail(attachment.url); setThumbnailId(attachment.id); }, "featured-image-upload")}
                        className="border-2 border-dashed border-gray-200 dark:border-white/10 rounded-lg p-6 text-center hover:border-[#ff0000]/30 dark:hover:border-[#ff4444]/30 transition-colors cursor-pointer"
                      >
                        <Image className="h-8 w-8 text-gray-300 dark:text-[#80858D] mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-[#A0A5AD]">
                          Click to upload featured image
                        </p>
                      </div>
                    )}
                    <input
                      id="featured-image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </AccordionItem>
                  <AccordionItem
                    title="Intro Video"
                    icon={Video}
                    open={accordionOpen.intro}
                    onToggle={() => toggleAccordion("intro")}
                  >
                    <div className="bg-gray-100 dark:bg-white/5 p-1 rounded-lg flex items-center mb-4 border border-gray-200/20">
                      <button
                        type="button"
                        onClick={() => setPromoVideoSource("upload")}
                        className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                          promoVideoSource === "upload"
                            ? "bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm border border-gray-200/50 dark:border-white/5"
                            : "text-gray-500 hover:text-gray-700 dark:text-[#A0A5AD] dark:hover:text-white"
                        }`}
                      >
                        Upload Video
                      </button>
                      <button
                        type="button"
                        onClick={() => setPromoVideoSource("url")}
                        className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                          promoVideoSource === "url"
                            ? "bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm border border-gray-200/50 dark:border-white/5"
                            : "text-gray-500 hover:text-gray-700 dark:text-[#A0A5AD] dark:hover:text-white"
                        }`}
                      >
                        Add from URL
                      </button>
                    </div>

                    {promoVideoSource === "upload" ? (
                      promoVideoUrl ? (
                        <div className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 aspect-video bg-gray-100 dark:bg-white/5 shadow-sm">
                          {(() => {
                            const embedUrl = getEmbedUrl(promoVideoUrl);
                            if (embedUrl) {
                              return (
                                <iframe
                                  src={embedUrl}
                                  className="w-full h-full"
                                  allowFullScreen
                                />
                              );
                            }
                            return (
                              <video src={promoVideoUrl} controls className="w-full h-full object-cover" />
                            );
                          })()}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => openWpMediaLibrary("video", (attachment) => { setPromoVideoUrl(attachment.url); }, "promo-video-upload")}
                              className="bg-white/90 hover:bg-white text-gray-800 text-[10px] font-semibold px-2 py-1 rounded shadow-md cursor-pointer border border-gray-200"
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setPromoVideoUrl("");
                                setPromoVideoHours(0);
                                setPromoVideoMinutes(0);
                                setPromoVideoSeconds(0);
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-semibold px-2 py-1 rounded shadow-md cursor-pointer"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => openWpMediaLibrary("video", (attachment) => { setPromoVideoUrl(attachment.url); }, "promo-video-upload")}
                          className="border-2 border-dashed border-gray-200 dark:border-white/10 rounded-lg p-6 text-center hover:border-red-500/30 dark:hover:border-red-500/30 transition-colors cursor-pointer space-y-2 bg-gray-50/30 dark:bg-white/3"
                        >
                          <Video className="h-6 w-6 text-gray-400 mx-auto" />
                          <span className="inline-block bg-white dark:bg-white/10 hover:bg-gray-50 dark:hover:bg-white/20 text-xs font-semibold text-gray-700 dark:text-[#D1D5DB] px-3 py-1.5 rounded-lg transition-colors border border-gray-200 dark:border-white/5 shadow-sm cursor-pointer">
                            Upload Video
                          </span>
                          <p className="text-[10px] text-gray-500 dark:text-[#A0A5AD] leading-relaxed">
                            MP4, and WebM formats, up to 300 MB
                          </p>
                        </div>
                      )
                    ) : (
                      <div className="space-y-3">
                        <Input
                          placeholder="Video URL (YouTube/Vimeo/Direct link)"
                          value={promoVideoUrl}
                          onChange={(e) => setPromoVideoUrl(e.target.value)}
                        />
                        {promoVideoUrl && (
                          <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-white/10 aspect-video bg-gray-100 dark:bg-white/5 shadow-sm">
                            {(() => {
                              const embedUrl = getEmbedUrl(promoVideoUrl);
                              if (embedUrl) {
                                return (
                                  <iframe
                                    src={embedUrl}
                                    className="w-full h-full"
                                    allowFullScreen
                                  />
                                );
                              }
                              return (
                                <video src={promoVideoUrl} controls className="w-full h-full object-cover" />
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                    <input
                      id="promo-video-upload"
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={handlePromoVideoUpload}
                    />

                    {/* Video Playback Time Section */}
                    <div className="space-y-2 mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
                      <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                        Video Playback Time
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex flex-col gap-1">
                          <input
                            type="number"
                            min={0}
                            placeholder="00"
                            value={promoVideoHours || ""}
                            onChange={(e) => setPromoVideoHours(Math.max(0, parseInt(e.target.value) || 0))}
                            className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-center text-xs font-semibold text-gray-700 dark:text-white shadow-sm focus:border-[#ff0000] focus:outline-none focus:ring-1 focus:ring-[#ff0000]"
                          />
                          <span className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider text-center">
                            Hours
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <input
                            type="number"
                            min={0}
                            placeholder="00"
                            value={promoVideoMinutes || ""}
                            onChange={(e) => setPromoVideoMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                            className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-center text-xs font-semibold text-gray-700 dark:text-white shadow-sm focus:border-[#ff0000] focus:outline-none focus:ring-1 focus:ring-[#ff0000]"
                          />
                          <span className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider text-center">
                            Mins
                          </span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <input
                            type="number"
                            min={0}
                            placeholder="00"
                            value={promoVideoSeconds || ""}
                            onChange={(e) => setPromoVideoSeconds(Math.max(0, parseInt(e.target.value) || 0))}
                            className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-center text-xs font-semibold text-gray-700 dark:text-white shadow-sm focus:border-[#ff0000] focus:outline-none focus:ring-1 focus:ring-[#ff0000]"
                          />
                          <span className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider text-center">
                            Secs
                          </span>
                        </div>
                      </div>
                    </div>
                  </AccordionItem>
                  <AccordionItem
                    title="Pricing Model"
                    icon={DollarSign}
                    open={accordionOpen.pricing}
                    onToggle={() => toggleAccordion("pricing")}
                  >
                    <div className="space-y-3">
                      <Select
                        options={[
                          { value: "paid", label: "Paid" },
                          { value: "free", label: "Free" },
                          { value: "subscription", label: "Subscription" },
                        ]}
                        value={pricingModel}
                        onChange={(val) => setPricingModel(val)}
                      />
                      {pricingModel !== "free" && (
                        <>
                          <Input
                            label="Regular Price"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Enter regular price"
                            value={regularPrice}
                            onChange={(e) => setRegularPrice(e.target.value)}
                          />
                          <Input
                            label="Sale Price"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="Enter sale price"
                            value={salePrice}
                            onChange={(e) => setSalePrice(e.target.value)}
                          />
                        </>
                      )}
                      {pricingModel === "free" && (
                        <p className="text-xs text-gray-400 italic py-2">This course is free — no pricing required.</p>
                      )}
                    </div>
                  </AccordionItem>
                  <AccordionItem
                    title="Categories"
                    icon={Tags}
                    open={accordionOpen.categories}
                    onToggle={() => toggleAccordion("categories")}
                  >
                    <Select
                      options={
                        dbCategories.length > 0
                          ? dbCategories.map((cat) => ({
                              value: cat.name,
                              label: cat.name,
                            }))
                          : [
                              { value: "এসএসসি", label: "এসএসসি" },
                              { value: "অষ্টম শ্রেণি", label: "অষ্টম শ্রেণি" },
                              { value: "সপ্তম শ্রেণি", label: "সপ্তম শ্রেণি" },
                              { value: "ষষ্ঠ শ্রেণি", label: "ষষ্ঠ শ্রেণি" },
                              { value: "Development", label: "Development" },
                              { value: "Design", label: "Design" },
                              { value: "Marketing", label: "Marketing" },
                            ]
                      }
                      value={category}
                      onChange={(val) => setCategory(val)}
                    />
                  </AccordionItem>
                  <AccordionItem
                    title="Tags"
                    icon={Tags}
                    open={accordionOpen.tags}
                    onToggle={() => toggleAccordion("tags")}
                  >
                    <Input
                      placeholder="Add tags separated by commas"
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                    />
                    {dbTags.length > 0 && (
                      <div className="mt-2.5">
                        <span className="text-[10px] font-semibold text-gray-400 dark:text-[#80858D] block mb-1">
                          Click to add existing tags:
                        </span>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pr-1">
                          {dbTags.map((tag) => (
                            <button
                              key={tag.term_id}
                              type="button"
                              onClick={() => {
                                const currentTags = tagsInput
                                  ? tagsInput.split(",").map((t) => t.trim()).filter(Boolean)
                                  : [];
                                if (!currentTags.includes(tag.name)) {
                                  currentTags.push(tag.name);
                                  setTagsInput(currentTags.join(", "));
                                }
                              }}
                              className="text-[10px] font-semibold bg-gray-100 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 text-gray-600 dark:text-[#D1D5DB] px-2.5 py-0.5 rounded-full transition-colors cursor-pointer border-0"
                            >
                              + {tag.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </AccordionItem>
                  <AccordionItem
                    title="Author"
                    icon={Users}
                    open={accordionOpen.author}
                    onToggle={() => toggleAccordion("author")}
                  >
                    <Select
                      options={
                        dbInstructors.length > 0
                          ? dbInstructors.map((inst) => ({
                              value: inst.name,
                              label: inst.name,
                            }))
                          : [
                              { value: "Sarah Johnson", label: "Sarah Johnson" },
                              { value: "Michael Chen", label: "Michael Chen" },
                              { value: "মোঃ ইউসুফ (BUET)", label: "মোঃ ইউসুফ (BUET)" },
                              { value: "ইমরান হোসেন (BUET)", label: "ইমরান হোসেন (BUET)" },
                            ]
                      }
                      value={selectedAuthor}
                      onChange={(val) => {
                        setSelectedAuthor(val);
                        setSelectedInstructors((prev) => {
                          if (prev.includes(val)) return prev;
                          return [...prev, val];
                        });
                      }}
                    />
                  </AccordionItem>
                  <AccordionItem
                    title="Instructors"
                    icon={Users}
                    open={accordionOpen.instructors}
                    onToggle={() => toggleAccordion("instructors")}
                  >
                    <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                      {(dbInstructors.length > 0
                        ? dbInstructors
                        : [
                            { id: 1, name: "Sarah Johnson" },
                            { id: 2, name: "Michael Chen" },
                            { id: 3, name: "মোঃ ইউসুফ (BUET)" },
                            { id: 4, name: "ইমরান হোসেন (BUET)" },
                          ]
                      ).map((inst) => {
                        const name = inst.name;
                        const isChecked = selectedInstructors.includes(name);
                        return (
                          <label
                            key={inst.id || name}
                            className="flex items-center gap-2 text-sm text-gray-700 dark:text-[#D1D5DB] cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedInstructors((prev) => [...prev, name]);
                                } else {
                                  setSelectedInstructors((prev) =>
                                    prev.filter((n) => n !== name)
                                  );
                                }
                              }}
                              className="rounded text-[#ff0000] focus:ring-[#ff0000]/30 border-gray-300 dark:bg-white/5 dark:border-white/20"
                            />
                            {name}
                          </label>
                        );
                      })}
                    </div>
                  </AccordionItem>
                </Card>
              </div>
            </>
          )}

          {/* ─── CURRICULUM TAB ─── */}
          {step === "curriculum" && (
            <div className="lg:col-span-12 space-y-6">
              <Card
                title="Course Curriculum"
                description="Organize your course with topics, lessons, and quizzes"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                  {/* Left Column: Create Topic Form */}
                  <div className="md:col-span-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
                      <h4 className="text-sm font-semibold text-gray-800">Create New Topic</h4>
                      <div className="space-y-4">
                        <Input
                          label="Topic Title"
                          placeholder="e.g. Introduction"
                          value={topicTitle}
                          onChange={(e) => setTopicTitle(e.target.value)}
                        />
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Topic Summary / Description
                          </label>
                          <textarea
                            placeholder="Add a summary..."
                            rows={3}
                            value={topicDescription}
                            onChange={(e) => setTopicDescription(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 resize-none"
                          />
                        </div>
                        <Button
                          onClick={handleAddTopic}
                          leftIcon={<Plus className="h-4 w-4" />}
                          className="w-full justify-center"
                        >
                          Add Topic
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Topics List */}
                  <div className="md:col-span-8 space-y-4">
                    {topics.length === 0 ? (
                      <div className="border border-dashed border-gray-200 rounded-xl p-8 text-center bg-gray-50/50">
                        <BookOpen className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                        <h4 className="text-sm font-medium text-gray-700">No Topics Added</h4>
                        <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                          Create topics using the form on the left. You can then add lessons and quizzes inside each topic.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
                        {topics.map((topic, index) => (
                          <div
                            key={topic.id}
                            className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm space-y-4"
                          >
                            {/* Topic Title & Actions */}
                            <div className="flex items-start justify-between border-b border-gray-100 pb-3">
                              <div className="space-y-0.5 flex-1 pr-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-bold bg-red-50 text-[#ff0000] px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    Topic {index + 1}
                                  </span>
                                </div>
                                <h3 className="text-sm font-bold text-gray-800">
                                  {topic.title}
                                </h3>
                                {topic.summary && (
                                  <p className="text-xs text-gray-500 line-clamp-2">
                                    {topic.summary}
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  type="button"
                                  disabled={index === 0}
                                  onClick={() => moveTopic(index, "up")}
                                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-gray-500 transition-colors"
                                  title="Move Up"
                                >
                                  <ChevronDown className="h-3.5 w-3.5 rotate-180" />
                                </button>
                                <button
                                  type="button"
                                  disabled={index === topics.length - 1}
                                  onClick={() => moveTopic(index, "down")}
                                  className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-gray-500 transition-colors"
                                  title="Move Down"
                                >
                                  <ChevronDown className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteTopic(topic.id)}
                                  className="p-1.5 rounded-lg border border-red-100 hover:bg-red-50 text-red-500 transition-colors"
                                  title="Delete Topic"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>

                            {/* Lessons List */}
                            <div className="space-y-2">
                              {topic.lessons && topic.lessons.length > 0 ? (
                                topic.lessons.map((lesson: any, lessonIdx: number) => (
                                  <div
                                    key={lesson.id}
                                    className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-gray-100/50 px-3.5 py-2.5 text-xs text-gray-700 transition-colors"
                                  >
                                    <span
                                      className="flex items-center gap-2 font-medium cursor-pointer hover:text-[#ff0000] transition-colors"
                                      onClick={() => handleStartEditLesson(topic.id, lesson)}
                                      title="Click to edit lesson details"
                                    >
                                      <BookOpen className="h-3.5 w-3.5 text-[#ff0000]" />
                                      <span className="underline decoration-dotted decoration-gray-400 hover:decoration-[#ff0000]">
                                        {lesson.title}
                                      </span>
                                      {lesson.duration && (
                                        <span className="text-[10px] text-gray-400 font-normal">
                                          • {lesson.duration}
                                        </span>
                                      )}
                                      {lesson.zoomMeeting && (
                                        <span className="inline-flex items-center gap-1 text-[9px] font-semibold bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">
                                          <Video className="h-2.5 w-2.5" /> Zoom
                                        </span>
                                      )}
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        disabled={lessonIdx === 0}
                                        onClick={() => moveLesson(topic.id, lessonIdx, "up")}
                                        className="p-1 hover:bg-gray-250 rounded disabled:opacity-40 text-gray-400"
                                        title="Move Lesson Up"
                                      >
                                        <ChevronDown className="h-3 w-3 rotate-180" />
                                      </button>
                                      <button
                                        type="button"
                                        disabled={lessonIdx === topic.lessons.length - 1}
                                        onClick={() => moveLesson(topic.id, lessonIdx, "down")}
                                        className="p-1 hover:bg-gray-250 rounded disabled:opacity-40 text-gray-400"
                                        title="Move Lesson Down"
                                      >
                                        <ChevronDown className="h-3 w-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteLesson(topic.id, lesson.id)}
                                        className="p-1 hover:bg-red-50 rounded text-red-500"
                                        title="Delete Lesson"
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-xs text-gray-400 italic py-1.5 text-center bg-gray-50/20 rounded-lg">
                                  No lessons added to this topic yet.
                                </p>
                              )}
                            </div>

                            {/* Quizzes List */}
                            {topic.quizzes && topic.quizzes.length > 0 && (
                              <div className="space-y-2">
                                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Quizzes</span>
                                {topic.quizzes.map((quiz: any) => (
                                  <div
                                    key={quiz.id}
                                    className="flex items-center justify-between rounded-lg border border-purple-100 bg-purple-50/50 px-3.5 py-2.5 text-xs text-gray-700"
                                  >
                                    <span className="flex items-center gap-2 font-medium">
                                      <HelpCircle className="h-3.5 w-3.5 text-purple-500" />
                                      {quiz.title}
                                      <span className="text-[10px] text-gray-400 font-normal">
                                        • {quiz.questionCount} questions • {quiz.totalPoints} pts
                                      </span>
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteQuiz(topic.id, quiz.id)}
                                      className="p-1 hover:bg-red-50 rounded text-red-500"
                                      title="Delete Quiz"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Actions within Topic */}
                            <div className="flex items-center gap-2 pt-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                leftIcon={<Plus className="h-3.5 w-3.5" />}
                                onClick={() => {
                                  setActiveTopicIdForLesson(topic.id);
                                  setShowLessonModal(true);
                                }}
                              >
                                Lesson
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                leftIcon={<HelpCircle className="h-3.5 w-3.5" />}
                                onClick={() => {
                                  setActiveTopicIdForQuiz(topic.id);
                                  setQuizTitle("");
                                  setQuizQuestions([{ id: Date.now(), question: "", options: ["", "", "", ""], correctAnswer: 0, points: 1 }]);
                                  setShowQuizModal(true);
                                }}
                              >
                                Quiz
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ─── ADDITIONAL TAB ─── */}
          {step === "additional" && (
            <>
              <div className="lg:col-span-8 space-y-6">
                <Card title="Additional Information">
                  {[
                    "What Will I Learn?",
                    "Target Audience",
                    "Requirements",
                    "Course Includes",
                  ].map((label) => (
                    <div key={label} className="mb-4 last:mb-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {label}
                      </label>
                      <textarea
                        rows={3}
                        placeholder={`Enter ${label.toLowerCase()}...`}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10"
                      />
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Total Course Duration (Hours)"
                      type="number"
                      defaultValue="24"
                    />
                    <Input
                      label="Total Course Duration (Minutes)"
                      type="number"
                      defaultValue="30"
                    />
                  </div>
                </Card>
              </div>
              <div className="lg:col-span-4">
                <Card title="Schedule Live Class">
                  <p className="text-sm text-gray-500 dark:text-[#A0A5AD] mb-4">
                    Connect with students via live Zoom sessions.
                  </p>
                  <Button
                    leftIcon={<Video className="h-4 w-4" />}
                    className="w-full"
                  >
                    Create a Zoom Meeting
                  </Button>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Lesson Creator Modal Overlay */}
      {showLessonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity overflow-y-auto">
          <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col my-8 transform transition-all animate-in fade-in zoom-in-95 duration-200 max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <span className="text-base font-bold text-gray-900">
                  {editingLessonId !== null ? "Edit Lesson" : "Add New Lesson"}
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  | Topic: {topics.find((t) => t.id === activeTopicIdForLesson)?.title || ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowLessonModal(false);
                  setActiveTopicIdForLesson(null);
                  // reset states
                  setLessonTitle("");
                  setLessonContent("");
                  setLessonDuration("15 min");
                  setLessonContentMode("visual");
                  setLessonFeaturedImage(null);
                  setLessonVideoSource("upload");
                  setLessonVideoUrl("");
                  setLessonVideoHours(0);
                  setLessonVideoMinutes(0);
                  setLessonVideoSeconds(0);
                  setLessonAttachments([]);
                  // Reset zoom states
                  setShowZoomSection(false);
                  setZoomMeetingName("");
                  setZoomMeetingSummary("");
                  setZoomMeetingDate("");
                  setZoomStartTime("");
                  setZoomDuration(40);
                  setZoomDurationUnit("minutes");
                  setZoomTimezone("Asia/Dhaka");
                  setZoomAutoRecording("none");
                  setZoomPassword("");
                  setZoomShowPassword(false);
                  setZoomHost("");
                  setLessonZoomMeeting(null);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleAddLesson} className="flex-1 flex flex-col overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-gray-200 flex-1 overflow-y-auto">
                
                {/* ─── LEFT COLUMN: MAIN CONTENT (65%) ─── */}
                <div className="md:col-span-8 p-6 space-y-5 bg-white">
                  <Input
                    label="Name"
                    placeholder="Enter Lesson Name"
                    value={lessonTitle}
                    onChange={(e) => setLessonTitle(e.target.value)}
                    required
                  />

                  <div className="space-y-2">
                    <label className="block text-sm font-medium !text-black">
                      Content
                    </label>
                    <div className="sh-light-editor border border-gray-300 rounded-xl overflow-hidden bg-white shadow-sm">
                      {/* Editor Header Bar */}
                      <div className="editor-toolbar flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => openWpMediaLibrary("attachment", (attachment) => {
                            const mediaUrl = attachment.url;
                            const filename = attachment.filename || "Media";
                            if (attachment.type === "image") {
                              setLessonContent((prev) => prev + `\n![${filename}](${mediaUrl})\n`);
                            } else if (attachment.type === "video") {
                              setLessonContent((prev) => prev + `\n<video src="${mediaUrl}" controls></video>\n`);
                            } else {
                              setLessonContent((prev) => prev + `\n[${filename}](${mediaUrl})\n`);
                            }
                            showToast("Media inserted into content editor!", "success");
                          }, "lesson-editor-media-upload")}
                          leftIcon={<Image className="h-3.5 w-3.5" />}
                          className="h-7 text-xs border-gray-200 hover:bg-gray-100 text-gray-700 bg-white"
                        >
                          Add media
                        </Button>
                        <input
                          id="lesson-editor-media-upload"
                          type="file"
                          accept="image/*,video/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              showToast("Uploading media to editor...", "info");
                              const formData = new FormData();
                              formData.append("file", file);
                              const nonce = (window as any).hubInstructor?.rest_nonce || (window as any).shLmsAdmin?.rest_nonce || (window as any).hubInstructor?.nonce;
                              const headers: Record<string, string> = {};
                              if (nonce) headers["X-WP-Nonce"] = nonce;
                              
                              const response = await fetch(getRestUrl("wp/v2/media"), {
                                method: "POST",
                                body: formData,
                                headers: headers,
                                credentials: "same-origin",
                              });
                              if (!response.ok) throw new Error("Upload failed");
                              const media = await response.json();
                              const mediaUrl = media.source_url || media.guid?.rendered;
                              setLessonContent((prev) => prev + `\n![${file.name}](${mediaUrl})\n`);
                              showToast("Media inserted into content editor!", "success");
                            } catch (err) {
                              showToast("Media upload failed.", "error");
                            }
                          }}
                        />

                        {/* Visual / Code Tabs Toggle */}
                        <div className="toggle-container flex rounded-md p-0.5">
                          <button
                            type="button"
                            onClick={() => setLessonContentMode("visual")}
                            className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${
                              lessonContentMode === "visual"
                                ? "active"
                                : ""
                            }`}
                          >
                            Visual
                          </button>
                          <button
                            type="button"
                            onClick={() => setLessonContentMode("code")}
                            className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${
                              lessonContentMode === "code"
                                ? "active"
                                : ""
                            }`}
                          >
                            Code
                          </button>
                        </div>
                      </div>

                      {/* Formatting Toolbar */}
                      <div className="editor-toolbar flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-gray-50 px-2 py-1.5 text-gray-500 animate-fade-in">
                        <select
                          onChange={(e) => handleHeadingChange(e.target.value)}
                          defaultValue="paragraph"
                          className="bg-transparent border-none outline-none text-xs font-semibold text-gray-700 mr-2 py-0.5 cursor-pointer"
                        >
                          <option value="paragraph">Paragraph</option>
                          <option value="heading1">Heading 1</option>
                          <option value="heading2">Heading 2</option>
                          <option value="heading3">Heading 3</option>
                        </select>
                        <div className="w-[1px] h-4 bg-gray-200 mx-1" />
                        {[
                          { label: "B", style: "font-bold", title: "Bold" },
                          { label: "I", style: "italic", title: "Italic" },
                          { label: "U", style: "underline", title: "Underline" },
                        ].map((btn) => (
                          <button
                            key={btn.label}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleLessonFormat(btn.label)}
                            className={`p-1 hover:bg-gray-100 hover:text-gray-900 rounded text-xs font-serif ${btn.style}`}
                            title={btn.title}
                          >
                            {btn.label}
                          </button>
                        ))}
                        <div className="w-[1px] h-4 bg-gray-200 mx-1" />
                        {/* List format mock */}
                        {["ul", "ol"].map((listType) => (
                          <button
                            key={listType}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleLessonFormat(listType)}
                            className="p-1 hover:bg-gray-100 hover:text-gray-900 rounded text-xs font-mono"
                            title={listType === "ul" ? "Bulleted List" : "Numbered List"}
                          >
                            {listType === "ul" ? "•—" : "1—"}
                          </button>
                        ))}
                        <div className="w-[1px] h-4 bg-gray-200 mx-1" />
                        {/* Quote, alignment, link mock */}
                        {["“", "≡L", "≡C", "≡R", "🔗", "🔓", "</>", "{}"].map((icon, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              if (icon === "🔗") {
                                handleLessonFormat("Link");
                              } else if (["≡L", "≡C", "≡R"].includes(icon)) {
                                handleLessonFormat(icon);
                              }
                            }}
                            className="px-1 py-0.5 hover:bg-gray-100 hover:text-gray-900 rounded text-xs"
                          >
                            {icon}
                          </button>
                        ))}
                      </div>

                      {/* Content Canvas */}
                      {lessonContentMode === "visual" ? (
                        <VisualEditor
                          value={lessonContent}
                          onChange={setLessonContent}
                          editorRef={lessonContentVisualRef}
                          placeholder="Write lesson description, references, or instructions..."
                          className="min-h-[220px]"
                        />
                      ) : (
                        <textarea
                          ref={lessonContentRef}
                          rows={10}
                          placeholder="Write lesson description, references, or instructions..."
                          value={lessonContent}
                          onChange={(e) => setLessonContent(e.target.value)}
                          className="w-full p-4 text-sm focus:outline-none resize-y border-none font-mono text-xs"
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* ─── RIGHT COLUMN: SIDEBAR METADATA (35%) ─── */}
                <div className="md:col-span-4 p-6 space-y-6 bg-gray-50/50 pb-20">
                  
                  {/* Featured Image Section */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Featured Image
                    </label>
                    {lessonFeaturedImage?.url ? (
                      <div className="relative group rounded-xl overflow-hidden border border-gray-200 aspect-video bg-gray-100">
                        <img src={lessonFeaturedImage.url} alt="Featured" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => openWpMediaLibrary("image", (attachment) => { setLessonFeaturedImage({ id: attachment.id, url: attachment.url }); }, "lesson-featured-image-upload")}
                            className="bg-white hover:bg-gray-100 text-xs font-semibold text-gray-800 px-3 py-1.5 rounded-lg cursor-pointer"
                          >
                            Change
                          </button>
                          <button
                            type="button"
                            onClick={() => setLessonFeaturedImage(null)}
                            className="bg-red-600 hover:bg-red-700 text-xs font-semibold text-white px-3 py-1.5 rounded-lg cursor-pointer"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => openWpMediaLibrary("image", (attachment) => { setLessonFeaturedImage({ id: attachment.id, url: attachment.url }); }, "lesson-featured-image-upload")}
                        className="border-2 border-dashed border-gray-300 hover:border-red-500/30 rounded-xl p-5 text-center bg-gray-50 hover:bg-gray-100/50 transition-colors cursor-pointer space-y-2"
                      >
                        <Image className="h-6 w-6 text-gray-400 mx-auto" />
                        <span className="inline-block bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 px-3 py-1.5 rounded-lg transition-colors border border-gray-200 shadow-sm">
                          Upload Image
                        </span>
                        <p className="text-[10px] text-gray-400 max-w-[200px] mx-auto leading-relaxed">
                          JPEG, PNG, GIF, and WebP formats, up to 300 MB
                        </p>
                      </div>
                    )}
                    <input
                      id="lesson-featured-image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLessonImageUpload}
                    />
                  </div>

                  {/* Video Meta Section */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Video
                    </label>
                    <div className="border border-gray-200 bg-white rounded-xl p-4 space-y-4 shadow-sm">
                      {/* Video Source Switcher */}
                      <div className="flex bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                        <button
                          type="button"
                          onClick={() => setLessonVideoSource("upload")}
                          className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            lessonVideoSource === "upload"
                              ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          Upload Video
                        </button>
                        <button
                          type="button"
                          onClick={() => setLessonVideoSource("url")}
                          className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            lessonVideoSource === "url"
                              ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          Add from URL
                        </button>
                      </div>

                      {lessonVideoSource === "upload" ? (
                        lessonVideoUrl ? (
                          <div className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-video bg-gray-100 shadow-sm">
                            <video src={lessonVideoUrl} controls className="w-full h-full object-cover" />
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => setLessonVideoUrl("")}
                                className="bg-red-600 hover:bg-red-700 text-white p-1 rounded shadow-md cursor-pointer"
                                title="Remove"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => openWpMediaLibrary("video", (attachment) => { setLessonVideoUrl(attachment.url); }, "lesson-video-upload")}
                            className="border-2 border-dashed border-gray-300 hover:border-red-500/30 rounded-xl p-4 text-center bg-gray-50 hover:bg-gray-100/50 transition-colors cursor-pointer space-y-2"
                          >
                            <Video className="h-6 w-6 text-gray-400 mx-auto" />
                            <span className="inline-block bg-white hover:bg-gray-50 text-xs font-semibold text-gray-700 px-3 py-1.5 rounded-lg transition-colors border border-gray-200 shadow-sm">
                              Upload Video
                            </span>
                            <p className="text-[10px] text-gray-500 leading-relaxed">
                              MP4, and WebM formats, up to 300 MB
                            </p>
                          </div>
                        )
                      ) : (
                        <div className="space-y-3">
                          <Input
                            placeholder="Video URL (e.g. YouTube, Vimeo, or MP4 URL)"
                            value={lessonVideoUrl}
                            onChange={(e) => setLessonVideoUrl(e.target.value)}
                          />
                          {lessonVideoUrl && (
                            <div className="relative rounded-lg overflow-hidden border border-gray-200 aspect-video bg-gray-100 shadow-sm">
                              {(() => {
                                const embedUrl = getEmbedUrl(lessonVideoUrl);
                                if (embedUrl) {
                                  if (embedUrl.includes("youtube.com/embed") || embedUrl.includes("player.vimeo.com")) {
                                    const isYouTube = embedUrl.includes("youtube.com/embed");
                                    return (
                                      <div className="w-full h-full relative overflow-hidden">
                                        <iframe
                                          src={embedUrl}
                                          style={isYouTube ? {
                                            position: 'absolute',
                                            width: '115%',
                                            height: '115%',
                                            top: '-7.5%',
                                            left: '-7.5%',
                                            maxWidth: 'none',
                                            maxHeight: 'none'
                                          } : {
                                            width: '100%',
                                            height: '100%'
                                          }}
                                          frameBorder="0"
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                          allowFullScreen
                                        />
                                        {isYouTube && (
                                          <div
                                            className="absolute bottom-0 right-0 w-[120px] h-[50px] bg-transparent z-[10] cursor-default"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                          />
                                        )}
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <video src={embedUrl} controls className="w-full h-full object-cover" />
                                    );
                                  }
                                }
                                return (
                                  <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center text-xs text-gray-500">
                                    <Video className="h-6 w-6 text-gray-400 mb-1" />
                                    <span>Invalid or unsupported video URL.</span>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                      <input
                        id="lesson-video-upload"
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={handleLessonVideoUpload}
                      />
                    </div>
                  </div>

                  {/* Video Playback Time Section */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Video Playback Time
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex flex-col gap-1">
                        <input
                          type="number"
                          min={0}
                          placeholder="00"
                          value={lessonVideoHours || ""}
                          onChange={(e) => setLessonVideoHours(Math.max(0, parseInt(e.target.value) || 0))}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-center text-xs text-gray-700 shadow-sm focus:border-[#ff0000] focus:outline-none focus:ring-1 focus:ring-[#ff0000] !bg-white"
                        />
                        <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider text-center">
                          Hours
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <input
                          type="number"
                          min={0}
                          max={59}
                          placeholder="00"
                          value={lessonVideoMinutes || ""}
                          onChange={(e) => setLessonVideoMinutes(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-center text-xs text-gray-700 shadow-sm focus:border-[#ff0000] focus:outline-none focus:ring-1 focus:ring-[#ff0000] !bg-white"
                        />
                        <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider text-center">
                          Mins
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <input
                          type="number"
                          min={0}
                          max={59}
                          placeholder="00"
                          value={lessonVideoSeconds || ""}
                          onChange={(e) => setLessonVideoSeconds(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-center text-xs text-gray-700 shadow-sm focus:border-[#ff0000] focus:outline-none focus:ring-1 focus:ring-[#ff0000] !bg-white"
                        />
                        <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider text-center">
                          Secs
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Free Lesson / Demo Toggle */}
                  <div className="flex items-center justify-between p-3.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl shadow-sm">
                    <div className="flex flex-col text-left">
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-200">ফ্রি ডেমো ক্লাস? (Free Preview)</span>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">অন করলে এটি কোর্সের ফ্রি ডেমো হিসেবে থাকবে</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={lessonIsFreePreview}
                        onChange={(e) => setLessonIsFreePreview(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#00C9A7]"></div>
                    </label>
                  </div>

                  {/* Exercise Files Section */}
                  <div className="space-y-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Exercise Files
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-center border-gray-200 hover:bg-gray-100 text-gray-700 bg-white shadow-sm"
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => openWpMediaLibrary("attachment", (attachment) => { setLessonAttachments((prev) => [...prev, { id: attachment.id, name: attachment.filename || attachment.title || "File", url: attachment.url }]); }, "lesson-attachment-upload")}
                    >
                      Upload Attachment
                    </Button>
                    <input
                      id="lesson-attachment-upload"
                      type="file"
                      className="hidden"
                      onChange={handleLessonAttachmentUpload}
                    />

                    {/* Attachments List */}
                    {lessonAttachments.length > 0 && (
                      <div className="space-y-1.5 pt-1.5">
                        {lessonAttachments.map((att) => (
                          <div
                            key={att.id}
                            className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-1.5 text-[10px] text-gray-600 border border-gray-200 shadow-sm"
                          >
                            <span className="truncate max-w-[160px]">{att.name}</span>
                            <button
                              type="button"
                              onClick={() => setLessonAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                              className="text-red-600 hover:text-red-700 ml-2"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Zoom Live Lesson Section */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setShowZoomSection(!showZoomSection);
                        if (!showZoomSection && !zoomMeetingName) {
                          setZoomMeetingName(lessonTitle || "Live Class");
                        }
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Video className="h-4 w-4 text-red-600" />
                        <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Zoom Live Lesson
                        </span>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                          showZoomSection ? "transform rotate-180" : ""
                        }`}
                      />
                    </button>

                    {showZoomSection && (
                      <div className="p-4 space-y-4 bg-white border-t border-gray-100">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Meeting Name *
                          </label>
                          <input
                            type="text"
                            required={showZoomSection}
                            placeholder="Enter Meeting Name"
                            value={zoomMeetingName}
                            onChange={(e) => setZoomMeetingName(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Meeting Summary (Optional)
                          </label>
                          <textarea
                            rows={2}
                            placeholder="Enter Description"
                            value={zoomMeetingSummary}
                            onChange={(e) => setZoomMeetingSummary(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                              Meeting Date *
                            </label>
                            <input
                              type="date"
                              required={showZoomSection}
                              value={zoomMeetingDate}
                              onChange={(e) => setZoomMeetingDate(e.target.value)}
                              className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                              Start Time *
                            </label>
                            <input
                              type="time"
                              required={showZoomSection}
                              value={zoomStartTime}
                              onChange={(e) => setZoomStartTime(e.target.value)}
                              className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                              Duration *
                            </label>
                            <input
                              type="number"
                              min={1}
                              required={showZoomSection}
                              value={zoomDuration}
                              onChange={(e) => setZoomDuration(Math.max(1, parseInt(e.target.value) || 0))}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">
                              Unit *
                            </label>
                            <select
                              value={zoomDurationUnit}
                              onChange={(e) => setZoomDurationUnit(e.target.value as "minutes" | "hours")}
                              className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                            >
                              <option value="minutes">Minutes</option>
                              <option value="hours">Hours</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Timezone
                          </label>
                          <select
                            value={zoomTimezone}
                            onChange={(e) => setZoomTimezone(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                          >
                            <option value="Asia/Dhaka">Asia/Dhaka (GMT+6)</option>
                            <option value="Asia/Kolkata">Asia/Kolkata (GMT+5:30)</option>
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">America/New_York (EST/EDT)</option>
                            <option value="Europe/London">Europe/London (GMT/BST)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Auto Recording
                          </label>
                          <select
                            value={zoomAutoRecording}
                            onChange={(e) => setZoomAutoRecording(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                          >
                            <option value="none">No Recording</option>
                            <option value="local">Local</option>
                            <option value="cloud">Cloud</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Meeting Password *
                          </label>
                          <div className="relative">
                            <input
                              type={zoomShowPassword ? "text" : "password"}
                              required={showZoomSection}
                              placeholder="Enter Password"
                              value={zoomPassword}
                              onChange={(e) => setZoomPassword(e.target.value)}
                              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 pr-8 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                            />
                            <button
                              type="button"
                              onClick={() => setZoomShowPassword(!zoomShowPassword)}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {zoomShowPassword ? (
                                <EyeOff className="h-3.5 w-3.5" />
                              ) : (
                                <Eye className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Meeting Host *
                          </label>
                          <select
                            required={showZoomSection}
                            value={zoomHost}
                            onChange={(e) => setZoomHost(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all"
                          >
                            <option value="">Select Host</option>
                            {(dbInstructors.length > 0
                              ? dbInstructors
                              : [
                                  { id: 1, name: "Sarah Johnson" },
                                  { id: 2, name: "Michael Chen" },
                                  { id: 3, name: "মোঃ ইউসুফ (BUET)" },
                                  { id: 4, name: "ইমরান হোসেন (BUET)" },
                                ]
                            ).map((inst) => (
                              <option key={inst.id || inst.name} value={inst.name}>
                                {inst.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <Button
                          type="button"
                          onClick={() => {
                            if (!zoomMeetingName || !zoomMeetingDate || !zoomStartTime || !zoomPassword || !zoomHost) {
                              showToast("Please fill all required Zoom meeting fields.", "error");
                              return;
                            }
                            const zoomData = {
                              meetingName: zoomMeetingName,
                              summary: zoomMeetingSummary,
                              date: zoomMeetingDate,
                              startTime: zoomStartTime,
                              duration: zoomDuration,
                              durationUnit: zoomDurationUnit,
                              timezone: zoomTimezone,
                              autoRecording: zoomAutoRecording,
                              password: zoomPassword,
                              host: zoomHost,
                            };
                            setLessonZoomMeeting(zoomData);
                            showToast("Zoom meeting created/updated for this lesson!", "success");
                          }}
                          className="w-full bg-[#ff0000] hover:bg-[#d60000] text-white text-xs py-1.5 rounded-lg shadow-sm"
                        >
                          Create/Update Meeting
                        </Button>
                      </div>
                    )}
                  </div>

                </div>

              </div>

              {/* Modal Actions Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50/50">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowLessonModal(false);
                    setActiveTopicIdForLesson(null);
                    // reset states
                    setLessonTitle("");
                    setLessonContent("");
                    setLessonDuration("15 min");
                    setLessonContentMode("visual");
                    setLessonFeaturedImage(null);
                    setLessonVideoSource("upload");
                    setLessonVideoUrl("");
                    setLessonVideoHours(0);
                    setLessonVideoMinutes(0);
                    setLessonVideoSeconds(0);
                    setLessonAttachments([]);
                    // Reset zoom states
                    setShowZoomSection(false);
                    setZoomMeetingName("");
                    setZoomMeetingSummary("");
                    setZoomMeetingDate("");
                    setZoomStartTime("");
                    setZoomDuration(40);
                    setZoomDurationUnit("minutes");
                    setZoomTimezone("Asia/Dhaka");
                    setZoomAutoRecording("none");
                    setZoomPassword("");
                    setZoomShowPassword(false);
                    setZoomHost("");
                    setLessonZoomMeeting(null);
                  }}
                  className="border-gray-200 text-gray-700 hover:bg-gray-100 bg-white shadow-sm"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#ff0000] hover:bg-[#d60000] text-white shadow-sm">
                  {editingLessonId !== null ? "Save Changes" : "Add Lesson"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quiz / MCQ Question Bank Builder Modal */}
      {showQuizModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity overflow-y-auto">
          <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col my-8 transform transition-all animate-in fade-in zoom-in-95 duration-200 max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-2">
                <span className="text-base font-bold text-gray-900">
                  Add New Quiz & MCQ
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  | Topic: {topics.find((t) => t.id === activeTopicIdForQuiz)?.title || ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowQuizModal(false);
                  setActiveTopicIdForQuiz(null);
                  setQuizTitle("");
                  setQuizTimeLimit(30);
                  setQuizPassingGrade(50);
                  setQuizRandomize(false);
                  setQuizQuestions([{ id: Date.now(), question: "", options: ["", "", "", ""], correctAnswer: 0, points: 1 }]);
                }}
                className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleAddQuiz} className="flex-1 flex flex-col overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-gray-200 flex-1 overflow-y-auto">
                
                {/* Left Column: Questions Builder */}
                <div className="md:col-span-8 p-6 space-y-6 bg-white">
                  <Input
                    label="Quiz Title"
                    placeholder="Enter Quiz Title"
                    value={quizTitle}
                    onChange={(e) => setQuizTitle(e.target.value)}
                    required
                  />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <span className="text-sm font-semibold text-gray-700">Questions Bank</span>
                      <span className="text-xs text-gray-500">{quizQuestions.length} Questions</span>
                    </div>

                    {quizQuestions.map((q, qIndex) => (
                      <div key={q.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50/30 space-y-4 relative">
                        {quizQuestions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setQuizQuestions((prev) => prev.filter((item) => item.id !== q.id));
                            }}
                            className="absolute top-4 right-4 text-xs font-semibold text-red-600 hover:text-red-800 transition-colors"
                          >
                            Remove
                          </button>
                        )}

                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                            Question {qIndex + 1} *
                          </label>
                          <input
                            type="text"
                            required
                            placeholder="Enter the question text"
                            value={q.question}
                            onChange={(e) => {
                              const val = e.target.value;
                              setQuizQuestions((prev) =>
                                prev.map((item) => (item.id === q.id ? { ...item, question: val } : item))
                              );
                            }}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-[#ff0000] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/10 transition-all shadow-sm"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-gray-500">
                            Answer Options & Correct Select *
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {q.options.map((opt, optIndex) => (
                              <div key={optIndex} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                                <input
                                  type="radio"
                                  name={`correct-ans-${q.id}`}
                                  checked={q.correctAnswer === optIndex}
                                  onChange={() => {
                                    setQuizQuestions((prev) =>
                                      prev.map((item) =>
                                        item.id === q.id ? { ...item, correctAnswer: optIndex } : item
                                      )
                                    );
                                  }}
                                  className="h-4 w-4 text-[#ff0000] focus:ring-[#ff0000]/30 border-gray-300"
                                />
                                <input
                                  type="text"
                                  required
                                  placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                                  value={opt}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setQuizQuestions((prev) =>
                                      prev.map((item) => {
                                        if (item.id === q.id) {
                                          const newOpts = [...item.options];
                                          newOpts[optIndex] = val;
                                          return { ...item, options: newOpts };
                                        }
                                        return item;
                                      })
                                    );
                                  }}
                                  className="flex-1 bg-transparent border-none outline-none text-xs text-gray-700 py-1"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="w-32">
                          <label className="block text-xs font-semibold text-gray-500 mb-1">
                            Points *
                          </label>
                          <input
                            type="number"
                            min={1}
                            required
                            value={q.points}
                            onChange={(e) => {
                              const val = Math.max(1, parseInt(e.target.value) || 1);
                              setQuizQuestions((prev) =>
                                prev.map((item) => (item.id === q.id ? { ...item, points: val } : item))
                              );
                            }}
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 focus:border-[#ff0000] focus:outline-none shadow-sm"
                          />
                        </div>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-center border-gray-200 hover:bg-gray-50 text-gray-700 bg-white shadow-sm"
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => {
                        setQuizQuestions((prev) => [
                          ...prev,
                          { id: Date.now(), question: "", options: ["", "", "", ""], correctAnswer: 0, points: 1 },
                        ]);
                      }}
                    >
                      Add Question
                    </Button>
                  </div>
                </div>

                {/* Right Column: Quiz Settings */}
                <div className="md:col-span-4 p-6 space-y-6 bg-gray-50/50 pb-20">
                  <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Quiz Settings
                  </span>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Time Limit (Minutes) *
                    </label>
                    <input
                      type="number"
                      min={1}
                      required
                      value={quizTimeLimit}
                      onChange={(e) => setQuizTimeLimit(Math.max(1, parseInt(e.target.value) || 0))}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#ff0000] focus:outline-none shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Passing Grade (%) *
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      required
                      value={quizPassingGrade}
                      onChange={(e) => setQuizPassingGrade(Math.max(1, Math.min(100, parseInt(e.target.value) || 0)))}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-[#ff0000] focus:outline-none shadow-sm"
                    />
                  </div>

                  <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                    <input
                      type="checkbox"
                      id="quizRandomize"
                      checked={quizRandomize}
                      onChange={(e) => setQuizRandomize(e.target.checked)}
                      className="h-4 w-4 rounded text-[#ff0000] focus:ring-[#ff0000]/30 border-gray-300"
                    />
                    <label htmlFor="quizRandomize" className="text-xs font-semibold text-gray-700 cursor-pointer">
                      Randomize Questions
                    </label>
                  </div>
                </div>
              </div>

              {/* Modal Actions Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50/50">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowQuizModal(false);
                    setActiveTopicIdForQuiz(null);
                    setQuizTitle("");
                    setQuizTimeLimit(30);
                    setQuizPassingGrade(50);
                    setQuizRandomize(false);
                    setQuizQuestions([{ id: Date.now(), question: "", options: ["", "", "", ""], correctAnswer: 0, points: 1 }]);
                  }}
                  className="border-gray-200 text-gray-700 hover:bg-gray-100 bg-white shadow-sm"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#ff0000] hover:bg-[#d60000] text-white shadow-sm">
                  Add Quiz
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
